// sessions-future.js — FUTURE sessions. Phase C/D vision. Unscheduled.
// Imported by sessions.js thin aggregator. Never import directly in components.
// Nothing here has a commit date or active work.
// To schedule: promote to sessions-pipeline.js with owner approval.
// Last updated: May 27, 2026 — DOC-SYNC · Railway prereqs updated (OCI VM-1 live May 4, 2026)

export const FUTURE_SESSIONS = [

  // ─── PHASE B DEFERRED ───────────────────────────────────────────────────
  // No OCI dependency — revisit after Phase B feature sessions complete

  {
    id: 'S27-CHART-TAB',
    sessionNumber: 'S27-CHART-TAB',
    name: 'Dedicated Chart Tab Page',
    status: 'planned',
    phase: 'Phase 5 — Screener Completion',
    items: [
      'Full-page /chart/:ticker tab within stock page (not separate viewport)',
      'Evaluate visx (Airbnb, MIT) as React component foundation for this tab',
      'If visx: React-first rendering alongside existing imperative D3 in PriceChart',
      'If pure D3: extend existing PriceChart with tab-specific layout',
      'Drawing toolbar prominent — designed with fib/S27I tools in mind',
      'mode=tab in PriceChart MODE_CONFIG (already defined — activate here)',
    ],
    prereqs: ['S27I deployed ✅ (fib tools need chart tab context)'],
    doD: ['Chart tab renders at /stocks/:ticker?tab=chart', 'All indicators + drawing tools work', 'visx decision documented'],
    smokeChecks: ['chart tab route in App.jsx', 'mode=tab renders in PriceChart'],
    files: ['src/components/PriceChart.jsx', 'src/pages/StockPage.jsx'],
    d3Signal: 'tab_chart_view → dedicated chart session → L2 commitment signal (higher conviction than embedded view)',
  },


  // ─── PHASE C — SCALE + REVENUE ──────────────────────────────────────────
  // Trigger: first paying subscriber → admin.marketpulze.com live

  {
    id: 'chart-engine-v2',
    sessionNumber: '02cd4d7',
    name: 'Chart Engine V2 — Custom Canvas Renderer (Own the Chart Completely)',
    status: 'planned',
    phase: 'Phase 7 — Revenue Gate',
    items: [
      'WHY: Brand freedom — no vendor logo or attribution ever. Full D3 signal integration.',
      'WHY: Unique look — chart matches MarketPulze brand, not another providers output',
      'WHY: Unlimited extension — any indicator, any drawing tool, any layout we want',
      'WHY: No vendor dependency — lightweight-charts roadmap does not control our features',
      'Architecture: D3.js coordinate math + HTML Canvas for rendering + React state for UI',
      'D3.js handles: time scale, price scale, zoom/pan, coordinate transforms',
      'Canvas handles: candles, volume bars, indicator lines, drawing layers',
      'Konva.js or Fabric.js evaluate for drawing interaction layer (trendlines, fibonacci)',
      'technicalindicators npm (MIT) for indicator math — calcBollinger, calcMACD, etc.',
      'Phase 1: Candlestick + volume + SMA overlays — feature parity with current S27E',
      'Phase 2: RSI + MACD sub-panels — feature parity with S27F',
      'Phase 3: Drawing tools — trendlines + horizontals + fibonacci — parity with S27G+S27I',
      'Phase 4: Full indicator suite — Bollinger + VWAP + EMA + templates — parity with S27H',
      'Migration: ChartPage.jsx (/chart/:ticker) uses V2. StockPage embedded chart stays V1 until V2 stable.',
      'All D3 signal logging (ChartInteractionLog, DrawingLog) preserved — same event schema',
      'Guest: full chart features visible with same lock design as current embedded chart',
      'Performance target: 60fps on 5Y weekly data (1,300 candles) on mid-range mobile',
    ],
    prereqs: [
      'S27H deployed — full chart page + all indicators live on lightweight-charts',
      'S27I deployed — fibonacci + scanner-chart chain live',
      'Phase C started — product has real subscribers, justifies investment',
      'OCI live — full OHLCV history available for stress-testing large datasets',
    ],
    doD: [
      '/chart/:ticker renders from custom canvas engine — no lightweight-charts dependency',
      'Feature parity: candlestick + volume + SMA + RSI + MACD + Bollinger + VWAP + EMA',
      'Drawing tools work: trendlines + horizontals + fibonacci — all DrawingLog events preserved',
      'Performance: 60fps on 1,300 candles on Chrome mobile simulation',
      'All ChartInteractionLog events fire with identical schema — D3 pipeline unaffected',
      'StockPage embedded chart: optional V2 upgrade or stay V1 (decision at session start)',
      'No branding, no attribution, no vendor logo anywhere on the chart',
      'CI=true npm run build clean',
    ],
    smokeChecks: [
      'test -f src/components/ChartEngineV2/index.jsx && echo "ChartEngineV2 component exists"',
      'grep -q "ChartEngineV2" src/pages/ChartPage.jsx && echo "ChartPage uses V2 engine"',
    ],
    files: [
      'src/components/ChartEngineV2/',
      'src/pages/ChartPage.jsx',
      'src/data/sessions.js',
    ],
    vendorDeps: [
      { name: 'D3.js', plan: 'BSD — already available', status: 'evaluate at session start' },
      { name: 'technicalindicators', plan: 'MIT — npm install', status: 'confirmed safe' },
      { name: 'Konva.js or Fabric.js', plan: 'MIT — evaluate for drawing layer', status: 'evaluate at session start' },
    ],
  },

  {
      id: 'mongodb-backup', sessionNumber: '02cd4d7',
      name: 'MongoDB Backup Strategy — Before Phase C Real Users',
      status: 'planned', phase: 'Phase 6 — Operational Hardening',
      items: [
        'MongoDB Atlas M0 free tier has ZERO automated backups',
        'DB corruption or accidental wipe = total data loss — real user data at risk',
        'Option A (recommended): upgrade to Atlas M2 $9/mo — automated daily backups',
        'Option B: weekly mongodump cron on OCI VM to OCI Object Storage (free)',
        'Test restore before marking done — untested backup is not a backup',
        'Document restore procedure in INCIDENT.md',
      ],
      prereqs: ['OCI migration OR Atlas M2 upgrade decision'],
      doD: ['Automated backup running', 'Test restore completed + documented in INCIDENT.md'],
      smokeChecks: [], files: ['INCIDENT.md'],
      vendorDeps: [{ name: 'MongoDB Atlas', plan: 'M2 $9/mo', status: 'upgrade before Phase C' }],
    },

  {
      id: 'adSense-prerequisites', sessionNumber: '02cd4d7',
      name: 'AdSense Prerequisites Checklist — Stage 2 Gate',
      status: 'planned', phase: 'Phase 6 — Operational Hardening',
      items: [
        'Apply for AdSense BEFORE S25 ships to guests — approval takes 2-4 weeks',
        'Requirement 1: Privacy Policy at /privacy URL — FIXED in Task 5',
        'Requirement 2: 20-30 pages real content — 229 stock pages covers this',
        'Requirement 3: Some publicly accessible pages — guest stock pages qualify',
        'Requirement 4: Disclaimers on every financial page — already in place',
        'Requirement 5: Cookie consent banner for EU visitors (add with GA4)',
        'Ad placement rules: guest pages only — NEVER /admin /roadmap /trading /profile',
        'Revenue target: $28/mo covers infra. $100/mo funds Phase D.',
      ],
      prereqs: ['google-analytics live', 'S27A ships to guests'],
      doD: ['AdSense account approved', 'Ad units on guest pages', 'Revenue tracked in GA4'],
      smokeChecks: [], files: ['src/pages/StockPage.jsx'],
      vendorDeps: [{ name: 'Google AdSense', plan: 'Revenue share ~$0.50-2 RPM', status: 'apply after prerequisites' }],
    },

  {
      id: '25',
      sessionNumber: 'S28A',
      name: 'Intelligence Dashboard + Alerts',
      status: 'planned',
      phase: 'Phase 7 — Revenue Gate',
      items: [
        'AI-generated daily market summary (Claude API)',
        'Sector rotation signals from 11-ETF heatmap data',
        'Personalised watchlist signals feed (members)',
        'Signal change alerts (email via SMTP2GO) when BULLISH→BEARISH',
        'Market breadth: VIX display, advance/decline',
        'AI Weekly Watchlist Ideas — top 3 BULLISH with narrative (replaces Motley Fool)',
      ],
      prereqs: ['S27D ✅ deployed', 'Phase B complete: /calendar, /news, /screener all live'],
      doD: [],
      smokeChecks: [],
      files: [],
      vendorDeps: [
        { name: 'Finnhub', plan: 'Free', endpoint: 'existing', status: 'confirmed' },
        { name: 'Anthropic Claude', plan: 'API', endpoint: 'summaries + alerts', status: 'confirmed' },
      ],
    },

  {
      id: '26',
      sessionNumber: 'S28B',
      name: 'Extended Stock Depth A — Analyst, Metrics, Peers, Dividends',
      status: 'planned',
      phase: 'Phase 7 — Revenue Gate',
      items: [
        'Section D: Analyst Price Targets (Finnhub /stock/price-target)',
        'Section A: Extended Metrics — PEG, EV/EBITDA, ROE, ROA, margins',
        'Section G: Peers Comparison — 6 peers side by side',
        'Section B: Dividend Panel — yield, payout ratio, 8-quarter history',
        'Label on Price Targets: "Wall Street consensus — not MarketPulze recommendation"',
      ],
      prereqs: ['S27D ✅', 'Finnhub free capacity confirmed (~10-11 calls/page within 60/min)'],
      doD: [],
      smokeChecks: [],
      files: [],
      vendorDeps: [
        { name: 'Finnhub', plan: 'Free', endpoint: '/stock/price-target, /stock/metric, /stock/peers, /stock/dividend', status: 'confirmed' },
      ],
    },

  {
      id: '27',
      sessionNumber: 'S28C',
      name: 'Extended Stock Depth B + Smart Money',
      status: 'planned',
      phase: 'Phase 7 — Revenue Gate',
      items: [
        'Section E: Performance vs Benchmark — 1W/1M/3M/6M/1Y/YTD vs SPY',
        'Section C: Splits History (Finnhub /stock/split)',
        'Section F: Insider Transactions — SEC Form 4, last 10 (Finnhub)',
        'Congressional Trading — elected official disclosures (FMP /senate-disclosure)',
        'RSI + MACD overlay on PriceChart.jsx (TradingView Lightweight Charts)',
        'Insider label: "SEC Form 4 — informational only. Not a buy/sell signal."',
      ],
      prereqs: [
        'Extended Stock Depth A (id: 26) ✅',
        'FMP Data Display Licensing Agreement signed',
      ],
      doD: [],
      smokeChecks: [],
      files: [],
      vendorDeps: [
        { name: 'Finnhub', plan: 'Free', endpoint: '/stock/insider-transactions, /stock/split', status: 'confirmed' },
        { name: 'FMP', plan: 'Basic $19/mo', endpoint: '/api/v4/senate-disclosure', status: 'pending licensing' },
      ],
    },

  {
      id: '28',
      sessionNumber: 'S28D',
      name: 'AI Scorecard Engine',
      status: 'planned',
      phase: 'Phase 7 — Revenue Gate',
      items: [
        '8-dimension weighted scoring — SEC EDGAR + Finnhub PRIMARY SOURCES ONLY',
        'Accounting Quality 20% | Auditor Health 15% | Related-Party Risk 15%',
        'Insider Activity 12% | Litigation 10% | Valuation vs Peers 12%',
        'Operational Momentum 10% | Catalyst Risk 6%',
        'Every score linked to source document + page + paragraph',
        'Members only — 2-dimension teaser for guests',
        'NEVER use news aggregators — primary documents only (EDGAR, PACER, Form 4)',
      ],
      prereqs: [
        'Extended Stock Depth B (id: 27) ✅',
        'FMP Data Display Licensing Agreement signed',
      ],
      doD: [],
      smokeChecks: [],
      files: [],
      vendorDeps: [
        { name: 'SEC EDGAR', plan: 'Free', endpoint: 'data.sec.gov — 10-K, 10-Q, 8-K', status: 'confirmed' },
        { name: 'FMP', plan: 'Basic $19/mo', endpoint: 'fundamentals, earnings, peers', status: 'pending licensing' },
        { name: 'Anthropic Claude', plan: 'API', endpoint: 'scoring engine + narrative', status: 'confirmed' },
      ],
    },

  {
      id: 'screener-ai-full',
      sessionNumber: 'S27D-SE3',
      name: 'Screener Enhancement 3 — Full AI Scanner with D3 Strategy Integration',
      status: 'planned',
      phase: 'Phase 7 — Revenue Gate',
      items: [
        'AI Scanner tab: natural language query — "Find stocks about to break out with high volume" → AI generates scan criteria',
        'AI generates: specific signal, score threshold, volume condition, sector focus based on current market regime',
        'Each AI-generated scan shows its reasoning: "Momentum Breakout scan: score > 70, volume > 1.5x avg, BULLISH signal, price within 5% of 52W high"',
        'Market regime context: AI adjusts scan aggressiveness based on Fear & Greed + VIX from MarketPage',
        'Scan backtesting: "This scan would have caught 8 of 10 top performers last week" (uses Stooq historical via OCI ATP)',
        'D3 integration: AI scanner results feed directly into D3 strategy queue — one click to add to D3 watchlist',
        'Screener alert: member sets "alert me when NVDA enters Top Bullish scan" — email via SMTP2GO',
        'D3 value: AI scan usage = member strategy sophistication signal — feeds D3 position sizing model',
      ],
      prereqs: ['S27D-SE2 done', 'OCI ATP live', 'Stooq historical loaded', 'Phase C complete'],
      doD: ['AI scan generates valid criteria', 'Market regime context shown', 'D3 watchlist integration', 'Alerts working'],
    },

  {
      id: 'pwa-mobile-app',
      sessionNumber: '02cd4d7',
      name: 'PWA — Progressive Web App (Add to Home Screen, Mobile-First)',
      status: 'planned',
      phase: 'Phase 6 — Operational Hardening',
      description: 'Native mobile app (iOS App Store / Google Play) has high cost and complexity. PWA achieves 90% of native experience at near-zero cost: add to home screen, offline capability, push notifications, native-feel navigation. React CRA already includes a service worker skeleton — activate it.',
      items: [
        '── WHY PWA NOT NATIVE APP ────────────────────────────────────────────',
        'Native iOS app: Apple developer account $99/yr + App Store review + Swift/React Native codebase + maintenance',
        'Native Android: Google Play $25 one-time + separate codebase or React Native',
        'PWA: zero app store cost, same React codebase, installable from browser, works offline',
        'PWA covers the emergency-stop use case: add to iPhone home screen → red button accessible from lock screen',
        '── PWA IMPLEMENTATION (React CRA) ───────────────────────────────────',
        'CRA already has public/manifest.json and src/serviceWorkerRegistration.js',
        'Step 1: update public/manifest.json — name, short_name, icons (192x192 + 512x512), theme_color, display: standalone',
        'Step 2: in index.js change serviceWorkerRegistration.unregister() → serviceWorkerRegistration.register()',
        'Step 3: create app icons (192px + 512px) with MarketPulze branding',
        'Step 4: test "Add to Home Screen" on iOS Safari and Android Chrome',
        '── OFFLINE STRATEGY ─────────────────────────────────────────────────',
        'Cache strategy: cache-first for static assets (JS/CSS/icons), network-first for API data',
        'Offline page: if network unavailable → show last cached market data with "data as of [timestamp]" label',
        'Never show stale signals as current — always label with age if from cache',
        '── PUSH NOTIFICATIONS (Phase D only) ───────────────────────────────',
        'D3 alert: "Emergency: D3 circuit breaker fired. 3 positions closed." → push to owner phone',
        'Price alert: member sets price target → push when hit',
        'Web Push API: free, no Firebase needed. Requires VAPID keys (generate once, store in OCI Vault)',
        'Legal: only send push with explicit consent. Clearly opt-in. Never auto-enable.',
        '── EMERGENCY STOP INTEGRATION ───────────────────────────────────────',
        'PWA home screen shortcut + /trading/emergency page = phone kill switch without needing a native app',
        'iOS Safari: Add to Home Screen → full-screen PWA → emergency stop one tap from lock screen',
      ],
      prereqs: ['mobile-responsive session complete (all pages work at 375px)', 'emergency-stop page built'],
      doD: [
        'manifest.json correctly configured',
        'Service worker registered and caching static assets',
        'Add to Home Screen tested on iOS Safari and Android Chrome',
        'Offline page shows with correct stale-data labeling',
        'Emergency stop accessible as PWA shortcut',
        'sessions.js: pwa-mobile-app → done',
      ],
      smokeChecks: [
        'grep -q "standalone" public/manifest.json && echo "PWA manifest configured" || echo "MISSING PWA manifest"',
        'grep -q "serviceWorkerRegistration.register" src/index.js && echo "Service worker registered" || echo "NOT registered"',
      ],
      files: ['public/manifest.json', 'src/index.js', 'src/serviceWorkerRegistration.js'],
      vendorDeps: [],
    },

  {
      id: 'growth-dashboard', sessionNumber: '02cd4d7',
      name: 'Growth Dashboard — Super Admin Metrics (Stop Building Blind)',
      status: 'future', phase: 'Phase 6 — Operational Hardening',
      items: [
        'Currently zero metrics on what is working — building completely blind',
        'Super Admin only — /roadmap or new /trading/metrics route',
        'Metrics: daily active members, invite request conversion rate',
        'Metrics: analysis runs/day, digest open rate, signal accuracy 30/60/90 day',
        'Metrics: guest to member conversion funnel (requires GA4)',
        'Metrics: top 10 most-analyzed tickers — drives SEO content priority',
        'Signal accuracy requires SignalLog from signal-log session — start NOW',
      ],
      prereqs: ['signal-log running 60+ days', 'google-analytics live'],
      doD: ['Dashboard shows real live data', 'Signal accuracy has 60+ days history'],
      smokeChecks: [], files: ['src/pages/RoadmapPage.jsx', 'backend/routes/admin.js'],
      vendorDeps: [],
    },

  {
      id: 'seo-content-strategy', sessionNumber: '02cd4d7',
      name: 'SEO Content Strategy — Phase C Compound Growth',
      status: 'future', phase: 'Phase 6 — Operational Hardening',
      items: [
        '229 SEO stock pages exist but identical structure — Google thin content risk',
        'Fix: unique AI paragraph per stock page regenerated weekly via Anthropic API',
        'Add sector hub pages: /sector/technology, /sector/healthcare etc',
        'Internal linking between related stocks and sector pages',
        'Dynamic meta descriptions per ticker',
        'Submit updated sitemap.xml to Google Search Console after each new page type',
        'These compound over time — start Phase C for Phase D organic traffic payoff',
      ],
      prereqs: ['Phase C stock depth features complete'],
      doD: ['Each stock page has unique AI content', 'Sector hub pages live', 'Sitemap updated'],
      smokeChecks: [], files: ['src/pages/StockPage.jsx', 'public/sitemap.xml'],
      vendorDeps: [{ name: 'Anthropic Claude API', plan: 'Usage ~$0.01/page/week', status: 'active' }],
    },

  {
      id: 'stripe-integration-spec', sessionNumber: '02cd4d7',
      name: 'Stripe Subscription Integration — Phase C End',
      status: 'future', phase: 'Phase 7 — Revenue Gate',
      items: [
        'User.subscriptionTier field exists (free/basic/premium) — MongoDB schema ready',
        'Build the payment flow before you need it — never launch billing cold',
        'Flow: Upgrade click -> Stripe Checkout -> webhook -> subscriptionTier updated',
        'Webhook: succeeded = tier up. payment_failed = grace period then graceful downgrade.',
        'Graceful downgrade: member -> free tier (not guest) if subscription lapses',
        'Tiers: Member free (invite-only) -> Pro $9 -> Premium $29 -> Elite $49',
        'Trigger: only build when 500+ members OR ad revenue > $100/mo',
        'Entity: will operate under Dsquare Inc when monetisation launches',
      ],
      prereqs: ['500+ members OR ad revenue > $100/mo', 'Phase C complete'],
      doD: ['Stripe checkout end-to-end tested', 'Downgrade flow tested', 'Invoices via SMTP2GO'],
      smokeChecks: ['grep -q "STRIPE_SECRET_KEY" backend/.env.example && echo "Stripe documented"'],
      files: ['backend/routes/billing.js', 'backend/.env.example'],
      vendorDeps: [{ name: 'Stripe', plan: '2.9% + 30c/tx', status: 'integrate at Phase C end' }],
    },


  // ─── PHASE D3-Infra — ENGINE INFRASTRUCTURE ─────────────────────────────

  {
      id: 'pdt-rule-manager',
      sessionNumber: '02cd4d7',
      name: 'PDT Rule Manager — Pattern Day Trader Compliance',
      status: 'planned',
      phase: 'Phase 9 — D3 Shadow',
      description: 'IBKR enforces PDT rule: accounts under $25,000 may execute maximum 3 day-trades per rolling 5 business days. Violation = IBKR locks account for 90 days. D3 momentum and pump strategies are day-trade heavy. Without a counter, D3 WILL trigger this lock on a small account.',
      items: [
        'PDT definition: opening AND closing a position in the same security on the same day = 1 day-trade',
        'PDT limit: 3 day-trades per rolling 5 business day window (not calendar week)',
        'Penalty: IBKR restricts account to closing-only orders for 90 days',
        'D3 hard rule: if account balance < $25,000 → track PDT counter rigorously',
        'Implementation: Redis counter pdt:day_trades with 5-day rolling window + MongoDB DayTradeLog',
        'Before EVERY momentum/pump execution: check PDT counter. If at 3 → ABORT, switch to non-day-trade strategy',
        'Alert owner when PDT counter reaches 2: "1 day-trade remaining this week — switching to overnight strategies"',
        'Mitigation options (document all three):',
        '  Option A: ensure account > $25,000 before D3a starts — PDT rule does not apply',
        '  Option B: use cash account instead of margin — PDT rule does not apply to cash accounts',
        '  Option C: actively manage counter — limit momentum/pump to 3 day-trades per week',
        'Recommended: Option A (account > $25k) OR Option B (cash account) — Option C is risky',
        'D3 startup check: on market open, fetch account balance via IBKR API. If < $25k → warn owner.',
      ],
      prereqs: ['D3-Infra: IB Gateway running, ib_insync connected'],
      doD: [
        'PDT counter tracking in Redis + MongoDB',
        'Execution gate checks counter before every day-trade',
        'Alert fires at counter = 2',
        'Hard ABORT at counter = 3 with strategy fallback to overnight',
        'Account balance check on startup',
        'sessions.js: pdt-rule-manager → done',
      ],
      smokeChecks: [
        'grep -qE "pdt|day_trade|PDT" backend/services/riskManager.js 2>/dev/null && echo "PDT manager exists" || echo "MISSING PDT manager"',
      ],
      files: ['backend/services/riskManager.js', 'backend/models/DayTradeLog.js'],
      vendorDeps: [],
    },

  {
      id: 'position-reconciliation',
      sessionNumber: '02cd4d7',
      name: 'Position Reconciliation — IB Gateway Restart Safety',
      status: 'planned',
      phase: 'Phase 9 — D3 Shadow',
      description: 'When IB Gateway disconnects and reconnects (reboot, network drop, PM2 restart), D3 in-memory position state may differ from what IBKR actually holds. Opening a duplicate position on a stock already held = double exposure. Missing a stop loss that fired during outage = undetected loss. Position reconciliation on every reconnect prevents both.',
      items: [
        '── ON EVERY IB GATEWAY CONNECT EVENT ────────────────────────────────',
        'Step 1: call ib.reqPositions() → get current IBKR account positions as source of truth',
        'Step 2: query MongoDB OpenPositions collection for what D3 believes it holds',
        'Step 3: compare the two sets. Three reconciliation cases:',
        '  MATCH: IBKR position = MongoDB position → proceed normally',
        '  IBKR HAS POSITION MongoDB MISSING: position was opened/modified during outage → add to MongoDB, alert owner',
        '  MONGODB HAS POSITION IBKR MISSING: position was closed (stop hit?) during outage → update MongoDB, alert owner',
        'Step 4: if ANY mismatch found → set D3_ENABLED=false, send email with reconciliation report, wait for owner approval',
        'Step 5: owner reviews positions in IBKR app and confirms accuracy → POST /api/trading/confirm-reconciliation → D3 resumes',
        'DO NOT resume automated trading with unreconciled positions — ever',
        '── HEARTBEAT MONITOR ────────────────────────────────────────────────',
        'Heartbeat: IB Gateway sends keepalive every 30 seconds. If missed 3 consecutive → trigger reconnect',
        'Reconnect sequence: (1) close ib_insync connection (2) wait 5 seconds (3) reconnect (4) run reconciliation',
        'Max reconnect attempts: 5. If all fail → emergency stop + email owner',
      ],
      prereqs: ['D3-Infra: IB Gateway running, MongoDB OpenPositions model exists'],
      doD: [
        'reqPositions() called on every IB Gateway connect',
        'Reconciliation logic comparing IBKR vs MongoDB state',
        'Mismatch → D3 halted + email alert',
        'Confirm-reconciliation endpoint for owner approval',
        'Heartbeat monitor with auto-reconnect running',
        'sessions.js: position-reconciliation → done',
      ],
      smokeChecks: [
        'test -f backend/models/OpenPositions.js && echo "OpenPositions model exists" || echo "MISSING"',
        'grep -qE "reqPositions|reconcil" backend/services/broker.js 2>/dev/null && echo "Reconciliation exists" || echo "MISSING"',
      ],
      files: ['backend/services/broker.js', 'backend/models/OpenPositions.js'],
      vendorDeps: [],
    },


  // ─── PHASE D3a — D3 SHADOW MODE ─────────────────────────────────────────

  {
      id: 'pump-detection-engine',
      sessionNumber: '02cd4d7',
      name: 'Pump & Dump Detection Engine — Volume + Social + Float + Filing Cross-Check',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'Systematic detection of coordinated pump campaigns (WhatsApp groups, social media, Discord). Detect early in the pump phase. Trade with micro size. Optionally short the dump. Fully autonomous with strict circuit breaker. This is Scenario A in the D3 4-scenario system.',
      items: [
        '── TRIGGER CONDITIONS (ALL must fire simultaneously) ──────────────────',
        'Volume spike > 500% vs 20-day average within < 30 minutes',
        'Social mention velocity: +400% above 30-day baseline within < 2 hours (StockTwits + Reddit)',
        'Float < 50M shares (low float = easy to move, typical pump target)',
        'No legitimate news on any verified source in last 4 hours (Bloomberg/Reuters/SEC EDGAR)',
        'Price move > 15% from open without fundamental catalyst in SEC filings',
        '── D3 ACTION — PUMP PHASE (early detection) ──────────────────────────',
        'Position size: MAXIMUM 0.5% of capital — this is HIGH risk, tiny size, never larger',
        'Entry: momentum confirmation = 2 consecutive green 1-min candles + RVOL > 5.0',
        'Stop loss: -5% from entry (pumps reverse in seconds — tight stop mandatory)',
        'Target: +15% gain OR exhaustion signal (volume declining + price flattening)',
        '── D3 ACTION — DUMP PHASE (reversal detected) ────────────────────────',
        'ABORT all longs immediately when: price making lower highs for 3 consecutive candles + volume still elevated',
        'PROCEED_SHORT if reversal confirmed: max 0.5% capital, cover at +10% gain or trend reversal',
        '── CIRCUIT BREAKER ───────────────────────────────────────────────────',
        'If pump strategy loses > 1% of total portfolio in a single day → PAUSE pump strategy 24 hours',
        'If 3 consecutive pump trades lose → disable pump strategy entirely, alert owner for manual review',
        '── LEGAL NOTE ────────────────────────────────────────────────────────',
        'D3 identifies and trades the market PATTERN. It never participates in or promotes manipulation.',
        'D3 never posts to pump groups. Never coordinates with anyone. Purely passive pattern detection.',
      ],
      d3Value: '500-1000% intraday moves on low-float stocks. 0.5% position at 3x = meaningful P&L at controlled risk.',
      legalNote: 'Identifying and trading pump patterns is legal. Participating in or promoting manipulation is securities fraud. D3 is always a passive, independent observer.',
      prereqs: [
        'D3a shadow mode complete (30+ days)',
        'social-integration live (Reddit + StockTwits monitoring active)',
        'Unusual Whales $50/mo subscribed (social + options anomaly confirmation)',
      ],
      doD: [
        'Pump detector running and logging PUMP_ALERT events to SignalLog',
        'Micro-size bracket orders via IBKRExecutor (0.5% max, tight stop)',
        'Dump phase reversal detection active',
        'Circuit breaker tested and confirmed working',
        'sessions.js: pump-detection-engine → done',
      ],
      smokeChecks: [
        'test -f backend/services/pumpDetector.js && echo "pumpDetector exists" || echo "MISSING pumpDetector"',
        'grep -q "PUMP_ALERT" backend/models/SignalLog.js 2>/dev/null && echo "PUMP_ALERT schema exists" || echo "MISSING"',
      ],
      files: ['backend/services/strategy.js', 'backend/services/pumpDetector.js', 'backend/models/SignalLog.js'],
      vendorDeps: [
        { name: 'StockTwits API', plan: 'Free', status: 'Required for mention velocity' },
        { name: 'Reddit API', plan: 'Free', status: 'Required for mention velocity' },
        { name: 'Unusual Whales', plan: '$50/mo', status: 'Social + options anomaly confirmation' },
      ],
    },

  {
      id: 'D3a',
      sessionNumber: '02cd4d7',
      name: 'D3a — Observer / Shadow Mode (No Execution)',
      status: 'future',
      phase: 'Phase 9 — D3 Shadow',
      items: [
        '★ Zero real money at risk — system logs decisions but executes NOTHING',
        '★ Run minimum 30 days before advancing to D3b. Gate: paper accuracy > 65%',
        '── SYSTEM PHILOSOPHY ──────────────────────────────────────────────────',
        'Veto Architecture: AI looks for reasons NOT to trade. Trade only if ALL veto checks pass.',
        'Negative Selection: Primary job is to DISQUALIFY trades, not find them.',
        'Correct target: Win rate 60-75%, Reward/Risk 2-3:1. NOT 90% win rate (90% is entry filter threshold).',
        '── 4 TRADING SCENARIOS ────────────────────────────────────────────────',
        'Scenario A — Inorganic Hype / Stock Promotion: Sentiment Velocity > 3.0 + no SEC filing in 24h → scalp/mean reversion',
        'Scenario B — Institutional Footprint / Momentum: RVOL > 2.0 + Price > VWAP → momentum entry with ATR trailing stop',
        'Scenario C — Spring Effect / Mean Reversion: RSI < 30 oversold + Bollinger squeeze → counter-trend entry, exit at 20MA',
        'Scenario D — Sector Sympathy / Statistical Arbitrage: ticker correlation to sector ETF > 0.85 but lagging by > 2 std devs → buy laggard',
        '── 4 VETO SPHERES (all must PASS — any Hard Veto kills the trade) ──────',
        'Sphere 1 Liquidity: OBI < -0.6 OR bid-ask spread > 1% OR dark pool divergence (block sells off-exchange + price rising) → VETO',
        'Sphere 2 Fundamental Integrity: CFO/CEO selling > 20% of stake in Form 4 OR convertible notes/death spiral financing in 10-Q → VETO',
        'Sphere 3 Crowding & Alternative: ticker trending #1 on StockTwits + Reddit simultaneously → VETO (alpha is gone). 3/5 sub-sector stocks at 52W lows → VETO',
        'Sphere 4 Macro & Systemic: VIX > 25 rising → Capital Preservation Mode. DXY + TNX both rising → VETO growth stocks. Credit spread spike > 10%/week → VETO all longs',
        '── CLAUDE AI GATE (claude-sonnet-4-20250514) ─────────────────────────',
        'Input: ticker + signal + score + delta + portfolio state + VIX + DXY + TNX + sector + daysToEarnings + sentiment + RVOL + OBI',
        'Output: { decision: PROCEED|HOLD|ABORT, confidence: 0-100, reasoning: 2 sentences, riskFlag: enum }',
        'Hard overrides Claude CANNOT bypass: ABORT if daysToEarnings < 2 | VIX > 30 | dayDrawdown > 8% | position > 10% portfolio | confidence < 65',
        'Economic calendar hard blackout: Fed/CPI/NFP/GDP day = tradingEnabled=false (hardcoded, not Claude decision)',
        'Time-of-day rules: No trades in first 5 minutes of market open (9:30-9:35 ET)',
        '── WHAT D3a BUILDS ────────────────────────────────────────────────────',
        'backend/services/strategy.js — 4 scenario detectors (A/B/C/D above)',
        'backend/services/tradeGate.js — Claude AI gate with all 4 veto spheres',
        'backend/services/riskManager.js — hard overrides + circuit breakers',
        'backend/services/broker.js — skeleton only (no execution in D3a)',
        'backend/services/filingScanner.js — SEC 8-K/10-Q/Form4 auditor (uses Claude claude-sonnet-4-20250514, NOT OpenAI)',
        'MongoDB ScenarioSignal collection — stores every signal detected with all veto results',
        'MongoDB TradeDecision collection — permanent audit log, never purged',
        '/trading/shadow dashboard — shows what AI would have traded vs actual market outcome',
        '── KEY FILING SCANNER LOGIC ───────────────────────────────────────────',
        'Hard veto keywords in 8-K: disagreement, resignation, restatement, default, termination → instant VETO',
        'Toxic financing in 10-Q: convertible notes, death spiral financing, at-the-market offering → VETO',
        'Truth Verification Cycle: Scenario flags ticker → FilingScanner pulls last 3 SEC filings → cross-check price vs filing truth',
      ],
      prereqs: [
        'D3-Infra complete: OCI VM live, IB Gateway running port 4001, Redis running, ib_insync handshake confirmed',
        'D0 used manually 60+ days with documented positive Sharpe in /trading/signals',
      ],
      doD: [
        '4 scenario detectors running and logging signals to MongoDB ScenarioSignal collection',
        'All 4 veto spheres active in tradeGate.js — every signal evaluated through all 4',
        'Claude AI Gate returning PROCEED/HOLD/ABORT decisions with full reasoning logged',
        'Filing scanner auditing SEC 8-K/Form4 for every candidate ticker',
        '/trading/shadow dashboard visible to Super Admin showing signals + vetoes + would-have-been outcomes',
        '30 days minimum runtime. Paper accuracy (PROCEED decisions that would have been profitable) > 65%',
        'sessions.js: D3a → done, D3b → next',
      ],
      smokeChecks: [
        'test -f backend/services/strategy.js && echo "strategy.js exists" || echo "MISSING"',
        'test -f backend/services/filingScanner.js && echo "filingScanner exists" || echo "MISSING"',
        'grep -qE "ScenarioSignal|TradeDecision" backend/models/TradeDecision.js 2>/dev/null && echo "MongoDB models exist" || echo "Models missing"',
      ],
      files: ['HANDOVER.md', 'src/data/sessions.js'],
      vendorDeps: [
        { name: 'SEC EDGAR', plan: 'Free public API', endpoint: 'data.sec.gov', status: 'No key needed' },
        { name: 'Finnhub', plan: 'Free tier', endpoint: '/calendar/earnings (earnings blackout dates)', status: 'Already in stack' },
        { name: 'Anthropic Claude', plan: 'Existing API key', endpoint: 'claude-sonnet-4-20250514 for AI Gate + FilingScanner', status: 'Existing — ~$10/mo at D3 scale' },
      ],
    },

  {
      id: 'counterfactual-log',
      sessionNumber: '02cd4d7',
      name: 'Counterfactual Trade Log — Veto Outcome Tracking',
      status: 'planned',
      phase: 'Phase 9 — D3 Shadow',
      description: 'When D3 vetoes a trade, it must record what would have happened if it had executed. Without this, veto sphere calibration is impossible — you cannot tell if an 85-95% veto rate is correct or too aggressive. This is the missing feedback mechanism that makes the self-learning loop complete.',
      items: [
        'Every VETO decision logged to MongoDB CounterfactualLog collection with full context',
        'Fields: ticker, timestamp, scenario_type, veto_sphere, veto_reason, entry_price_at_veto, signal_score',
        'Hourly job: for every open counterfactual → fetch current price → calculate hypothetical P&L',
        'Close counterfactual at: +24 hours (day trade), +5 days (speculation), +30 days (value)',
        'Outcome fields: final_price, hypothetical_pnl, hypothetical_pnl_pct, outcome_classification',
        'Outcome classifications: VETO_CORRECT_LOSS_AVOIDED / VETO_CORRECT_VOLATILE_AVOIDED / VETO_OVERCAUTIOUS_MISSED_GAIN / VETO_NEUTRAL',
        'Weekly analysis: what % of vetoes were correct? Which veto sphere fires most? Which fires wrong most often?',
        'Calibration input: if VETO_OVERCAUTIOUS > 30% over 90 days → that veto sphere sensitivity needs loosening',
        'Calibration input: if VETO_CORRECT_LOSS_AVOIDED < 50% → that veto sphere sensitivity needs tightening',
        '/trading/shadow dashboard: add "Veto Review" tab showing counterfactual outcomes by sphere',
        'CRITICAL: do not adjust veto weights based on fewer than 30 counterfactual outcomes per sphere — insufficient sample',
      ],
      d3Value: 'Without counterfactual logging, the self-learning loop is blind. This is the feedback that makes veto calibration possible.',
      prereqs: ['D3a shadow mode running — must log from day 1 of D3a'],
      doD: [
        'CounterfactualLog collection in MongoDB with all fields',
        'Hourly price fetch job updating open counterfactuals',
        'Outcome classification working for all 4 outcome types',
        'Weekly analysis report in /trading/shadow',
        'sessions.js: counterfactual-log → done',
      ],
      smokeChecks: [
        'test -f backend/models/CounterfactualLog.js && echo "CounterfactualLog model exists" || echo "MISSING"',
      ],
      files: ['backend/models/CounterfactualLog.js', 'backend/services/tradeGate.js'],
      vendorDeps: [],
    },


  // ─── PHASE D3b — D3 SCENARIOS ───────────────────────────────────────────

  {
      id: 'D3b',
      sessionNumber: '02cd4d7',
      name: 'D3b — Paper Trading (Alpaca Paper, Full System Active)',
      status: 'future',
      phase: 'Phase 10 — D3 Paper',
      items: [
        '★ First real execution — Alpaca paper account (zero real money, but real execution logic)',
        '★ Run minimum 60 days. Gate: Sharpe > 0.8 over full 60-day period. Max drawdown < 10%.',
        '── IBKR EXECUTOR (ib_insync bracket orders) ───────────────────────────',
        'IBKRExecutor class: ib_insync library, connects to IB Gateway port 4001 on OCI VM',
        'Every trade born as a bracket order: parent limit entry + take profit limit + stop loss stop',
        'parent.transmit = False until all 3 legs bundled. sl_order.transmit = True triggers the bundle.',
        'Execution type: Adaptive Algo orders (IBKR server finds best price in spread, reduces slippage 0.1-0.5%)',
        'In D3b: Alpaca paper API used for execution. IBKRExecutor logs decisions but does not fire live.',
        '── HALF-KELLY POSITION SIZING ─────────────────────────────────────────',
        'Formula: f* = (p(b+1)-1)/b / 2  where p = Claude confidence/100, b = win/loss ratio from MongoDB TradeHistory',
        'Half-Kelly applied (divide full Kelly by 2) — accounts for model overconfidence and estimation error',
        'Example: p=0.72, b=2.1 → f* = (0.72×3.1-1)/2.1/2 = 2.2%/2 = 1.1% of portfolio per trade',
        'Never risk more than 2% per trade regardless of Kelly output — hard cap',
        '── CRASH DETECTOR (CrashDetector class) ──────────────────────────────',
        'OBI = (BidVolume - AskVolume) / (BidVolume + AskVolume) — calculated from IBKR Level 2 stream',
        'If OBI < -0.6 while price falling → Liquidity Gap → cancel all pending buy orders, exit longs immediately',
        'If RVOL Z-score > 3.0 + bid-ask spread widening rapidly → Forced Liquidation signature → move to 100% cash',
        'If price drops > 3.5 standard deviations in 1 minute + spread > 2x avg → EMERGENCY_EXIT (flash crash)',
        '── EXPECTATION ENGINE ─────────────────────────────────────────────────',
        'On trade entry: generate Expected Price Path (EPP) for next 15/30/60 minutes',
        'Expected slope = entry_price + (expected_std_dev × 0.5 × time_steps) — conservative projection',
        'Reality Check every 100ms: if actual_volume < 60% of expected_volume at target price → VETO_WEAK_VOLUME → exit',
        'Velocity Check: if current_price < expected_price × 0.98 → VETO_SLOW_MOMENTUM → exit',
        'Smart Exit (Exhaustion Signal): last 3 minutes show high buy volume but price making lower highs → buyers exhausted → exit',
        '── POSITION SCALING (Progressive Entry) ──────────────────────────────',
        'Enter 30% initial position → confirm trend for 2-3 minutes → enter 40% → enter remaining 30%',
        'Reduces false signal entries significantly vs full position on first tick',
        '── MASTER DECISION DASHBOARD (MDD) ───────────────────────────────────',
        '/trading/mdd — Super Admin only React dashboard on MarketPulze frontend',
        'View 1 Live Confluence Map: all active tickers with probability scores (Ps) per scenario',
        'View 2 Mind Eye: real-time chart overlaying Expected Price Path (EPP) vs actual tick data',
        'View 3 Veto Stream: scrolling audit log (VETO: Toxic 8-K / VETO: HFT Spoofing / VETO: OBI collapse)',
        'View 4 Portfolio Exposure: current positions, P&L, Kelly fractions, daily drawdown',
        'Confidence Corridor: green zone = EPP, flashes RED when price exits zone downside = AI seeking exit',
        '── RISK MANAGER LIMITS ────────────────────────────────────────────────',
        'Max 2% loss per trade (Half-Kelly hard cap) | Max 5 concurrent positions | Daily kill switch at 5% portfolio loss',
        'Max single stock 10% | Max single sector 30% | Earnings blackout 48h | No trades 9:30-9:35 ET',
      ],
      prereqs: [
        'D3a complete: 30+ days shadow mode, paper accuracy > 65%',
        'All 4 veto spheres validated in shadow mode — veto rate should be 85-95% (system rejects most trades)',
        'IBKRExecutor class tested with 1-share paper trades to verify bracket order logic',
      ],
      doD: [
        'Alpaca paper account executing real bracket orders (limit entry + TP + SL bundle)',
        'Half-Kelly sizing calculating correctly from TradeHistory win/loss ratio',
        'CrashDetector running: OBI < -0.6 triggers emergency exit within 2 seconds',
        'ExpectationEngine generating price paths and exiting weak/slow trades automatically',
        'MDD dashboard (/trading/mdd) showing live confluence map + veto stream',
        'Post-Mortem agent running at 4:30 PM — Claude writes daily report to /trading/reports',
        '60 days minimum. Sharpe > 0.8 over full period. Max drawdown did not exceed 10%.',
        'sessions.js: D3b → done, D3c → next',
      ],
      smokeChecks: [
        'grep -qE "IBKRExecutor|execute_bracket" backend/services/broker.js 2>/dev/null && echo "IBKRExecutor exists" || echo "MISSING"',
        'grep -qE "halfKelly|kelly_fraction" backend/services/riskManager.js 2>/dev/null && echo "Kelly sizing exists" || echo "MISSING"',
        'grep -qE "CrashDetector|OBI|emergency_exit" backend/services/riskManager.js 2>/dev/null && echo "CrashDetector exists" || echo "MISSING"',
        'grep -qE "ExpectationEngine|expectedPath|VETO_WEAK_VOLUME" backend/services/tradeGate.js 2>/dev/null && echo "ExpectationEngine exists" || echo "MISSING"',
      ],
      files: ['HANDOVER.md', 'src/data/sessions.js'],
      vendorDeps: [
        { name: 'Alpaca Paper', plan: 'Free', endpoint: 'paper-api.alpaca.markets', status: 'D3b execution' },
        { name: 'IBKR Level 2', plan: '~$15-30/mo', endpoint: 'IB Gateway streaming', status: 'OBI calculation' },
      ],
    },

  {
      id: 'social-integration',
      sessionNumber: '02cd4d7',
      name: 'Social Integration — X, Reddit, StockTwits Read + Post + Feedback Loop',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'Bidirectional social integration. READ social signals for pump detection + speculation strategy. POST D3 signal cards to build feedback loop. Community reaction to D3 posts becomes a confirmation/contrarian signal. Feedback loop makes D3 self-aware of crowd sentiment toward its own decisions.',
      items: [
        'READ — Reddit API (free): r/wallstreetbets, r/pennystocks, r/stocks — ticker mention velocity per hour vs 30-day baseline',
        'READ — StockTwits API (free): message volume + sentiment per ticker — primary pump detection social signal',
        'READ — X/Twitter Basic API ($200/mo): real-time cashtag monitoring — subscribe at D3b only, not before',
        'Pump detection formula: ticker mentions +400% in <2hrs + no SEC news + float <50M + volume spike = PUMP_ALERT',
        'POST — D3 signal cards to X + StockTwits: "Bullish signal detected on $TICKER — momentum breakout [algorithmic signal, not investment advice]"',
        'FEEDBACK LOOP: read community reaction to D3 posts → contrarian or confirmation signal weight',
        'Self-calibrating: when community disagreed with D3, who was right? Track over 60 days.',
        'Social signals feed D3 T3 Sentiment gate alongside congressional + insider + member data',
        'DO NOT post trade results. DO NOT recommend securities. ALWAYS include legal disclaimer.',
      ],
      d3Value: 'Pump detection requires social velocity. Feedback loop creates unique self-improving social signal no vendor replicates.',
      legalNote: 'Never post trade results. Never recommend securities. Always: "algorithmic signal, not investment advice."',
      prereqs: ['D3a complete — 30+ days shadow mode', 'SignalLog running 60+ days'],
      doD: [
        'Reddit + StockTwits mention velocity monitoring active and logging to SignalLog',
        'D3 signal cards posting to social with legal disclaimer',
        'Feedback loop logging community reaction vs D3 outcome',
        'Pump detection integrated into D3 T3 gate',
        'sessions.js: social-integration → done',
      ],
      smokeChecks: [],
      files: ['backend/services/social.js', 'backend/services/tradeGate.js'],
      vendorDeps: [
        { name: 'Reddit API', plan: 'Free', endpoint: '/r/wallstreetbets + /r/pennystocks', status: 'Phase D3b' },
        { name: 'StockTwits API', plan: 'Free', endpoint: '/streams/symbol/{symbol}', status: 'Phase D3b' },
        { name: 'X/Twitter Basic API', plan: '$200/mo', endpoint: 'Real-time filtered stream', status: 'Evaluate ROI before subscribing at D3b' },
      ],
    },

  {
      id: 'scenario-e-earnings',
      sessionNumber: '02cd4d7',
      name: 'Scenario E — Earnings Play Strategy (Pre-Earnings Drift + PEAD)',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'Earnings events create two distinct and repeatable trading patterns. Pre-earnings drift: stock drifts toward analyst consensus price in 3-5 days before the print. PEAD (Post-Earnings Announcement Drift): stocks that beat estimates continue drifting in the same direction for 3-5 days. Both are separable from the general speculation strategy and have tighter timing rules.',
      items: [
        '── PATTERN 1: PRE-EARNINGS DRIFT (enter 72h pre-print, exit before print) ─',
        'Entry condition: earnings in 3-5 days + analyst consensus beat probability > 65% + RVOL > 1.2',
        'Entry type: small position, 72 hours before earnings print (never hold through)',
        'Hard exit rule: close 100% of position by market close the day BEFORE earnings — no exceptions',
        'Stop loss: -2% from entry. Risk: 0.5-1% of portfolio max',
        'Economic calendar integration: Finnhub /calendar/earnings endpoint feeds this strategy',
        '── PATTERN 2: PEAD — Post-Earnings Announcement Drift ─────────────────',
        'Entry condition: earnings just reported (within 2 hours) + beat by >5% on EPS + stock up >3% AH',
        'Entry type: next market open if gap is orderly (not gapping >10% — too risky)',
        'Hold period: 3-5 days then reassess. Exit if momentum stalls or stock gives back >50% of gap',
        'Stop loss: below the post-earnings gap low. Risk: 1-2% of portfolio max',
        '── HARD BLACKOUT RULE ────────────────────────────────────────────────',
        'All OTHER D3 strategies: 48-hour blackout around any earnings event for that ticker',
        'Scenario E specifically: this strategy IS the earnings play — replace blackout with entry signal',
        '── SCENARIO E VETO SPHERE ADDITIONS ─────────────────────────────────',
        'Veto if: company has >3 consecutive earnings misses in last 12 months (serial disappointer)',
        'Veto if: guidance reduction in last 2 quarters even with beat (beat + lower guidance = sell)',
        'Veto if: sector broadly weak day of earnings (sector ETF down >1% on earnings day)',
      ],
      d3Value: 'Earnings are the most predictable recurring catalyst. Two repeatable patterns with defined entry/exit rules.',
      prereqs: ['D3a: shadow mode complete', 'Finnhub earnings calendar integrated'],
      doD: [
        'Pre-earnings drift detector running and logging to ScenarioSignal',
        'PEAD detector watching for beat+gap pattern',
        'Hard exit rules enforced (cannot hold through print)',
        'Veto overrides for serial disappointers and guidance cuts',
        'sessions.js: scenario-e-earnings → done',
      ],
      smokeChecks: [
        'grep -qE "PEAD|pead|pre.earnings|preEarnings" backend/services/strategy.js 2>/dev/null && echo "Scenario E exists" || echo "MISSING"',
      ],
      files: ['backend/services/strategy.js', 'backend/services/tradeGate.js'],
      vendorDeps: [{ name: 'Finnhub', plan: 'Free', endpoint: '/calendar/earnings', status: 'Already integrated' }],
    },

  {
      id: 'scenario-f-short-squeeze',
      sessionNumber: '02cd4d7',
      name: 'Scenario F — Short Squeeze + Institutional Accumulation (3 Sub-Modes)',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'High short float + institutional ownership > 80% + good fundamentals = 3 separable setups. Mode 1 (long accumulation), Mode 2 (squeeze trigger), Mode 3 (contrarian short when fundamentals crack). Must identify which mode is active before entering.',
      items: [
        '── SETUP IDENTIFICATION ─────────────────────────────────────────────',
        'Short float > 10% + institutional ownership > 80% + combined float ratio > 100% + DTC > 5',
        'CRITICAL: high short interest alone is NOT a buy signal. Confirm fundamentals before Mode 1/2.',
        '── MODE 1: LONG ACCUMULATION (weeks to months, 2-4% size) ──────────',
        'Conditions: short float >10% + institutional >80% + fundamentals STRONG + stock near support',
        'Entry: scale in 3 tranches 40/30/30% over 3-5 days. Stop: below support/52W low.',
        'Target: analyst consensus OR +20% take partial, trail rest. D3 tag: LONG_ACCUMULATION',
        '── MODE 2: SQUEEZE TRIGGER (days to weeks, 1.5-2% size) ─────────────',
        'Conditions: Mode 1 setup + CATALYST fires: earnings beat / contract / upgrade / FDA',
        'Entry: limit order first 5 minutes. Stop: below pre-catalyst price.',
        'Target: short float × avg daily vol = theoretical squeeze target. 50% at +15%, trail rest.',
        'D3 tag: SQUEEZE_TRIGGER',
        '── MODE 3: CONTRARIAN SHORT (days to weeks, 1-1.5% size) ───────────',
        'Conditions: high short interest BUT revenue declining OR margin compression OR guidance cut',
        'The short sellers are RIGHT. Entry: breakdown below 20-day low OR earnings miss.',
        'Stop: above catalyst high (squeeze risk real). VETO if DTC > 10 days.',
        'D3 tag: CONTRARIAN_SHORT',
        '── VETO ALL MODES ───────────────────────────────────────────────────',
        'VETO Mode 1+2 if: toxic financing in 10-Q / PE > 3x sector / SEC investigation',
        'VETO Mode 3 if: DTC > 10 days (squeeze risk too high)',
        'VETO ALL if: spread > 1% (cannot exit cleanly)',
      ],
      d3Value: 'Unique combination of fundamental + market structure data. Three distinct P&L profiles in one setup.',
      legalNote: 'Short selling is legal. Spreading false information is not. D3 never communicates positions publicly.',
      prereqs: ['D3a complete', 'Quiver $10/mo (short interest)', 'EDGAR 13F parser (Phase C prereq)', 'Fast news feed for catalyst detection'],
      doD: [
        'Short float + institutional % + DTC computed daily',
        'Mode 1/2/3 detection in strategy.js with correct tags',
        'All veto conditions active',
        'sessions.js: scenario-f-short-squeeze → done',
      ],
      smokeChecks: [
        'grep -qE "SQUEEZE_TRIGGER|CONTRARIAN_SHORT|LONG_ACCUMULATION" backend/services/strategy.js 2>/dev/null && echo "Scenario F exists" || echo "MISSING"',
      ],
      files: ['backend/services/strategy.js', 'backend/services/tradeGate.js'],
      vendorDeps: [
        { name: 'Quiver Quantitative', plan: '$10/mo', endpoint: '/short-interest', status: 'PLANNED Phase C' },
        { name: 'SEC EDGAR', plan: 'Free', endpoint: '13F filings + FINRA short data', status: 'ACTIVE' },
      ],
    },

  {
      id: 'scenario-g-insider-cluster',
      sessionNumber: '02cd4d7',
      name: 'Scenario G — Insider Cluster Buying Signal',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'Insider cluster buying — but with strict filters to eliminate PR buys, token purchases, and coordinated pump schemes. Only genuine conviction purchases count. Includes biotech-specific rules for pre-catalyst accumulation which is the single strongest signal in that sector.',
      items: [
        '── STEP 1: FILTER OUT FAKE / PUMP INSIDER BUYS ─────────────────────',
        'DISCARD: purchase value < $25,000 per individual transaction (token/PR buy — not conviction)',
        'DISCARD: shares bought < 500 in a single transaction for stocks priced > $10 (cosmetic)',
        'DISCARD: same insider has filed 4+ purchases in 90 days all < $10,000 each (drip-pump pattern)',
        'DISCARD: purchase within 7 days of company issuing a press release about the insider buying (PR stunt)',
        'DISCARD: purchase by non-executive board members buying < $50,000 (directors often buy $500-5000 quarterly for optics)',
        'DISCARD: 10b5-1 prescheduled plan purchases (algorithmic, not discretionary)',
        'DISCARD: DRIP dividend reinvestment automatic purchases',
        'DISCARD: stock awarded as compensation (transaction_code = A, not P)',
        'KEEP ONLY: transaction_code = P (open-market purchase) + value >= $25,000 + not prescheduled',
        '',
        '── STEP 2: CLASSIFY THE QUALIFYING CLUSTER ─────────────────────────',
        'CLUSTER_WEAK:   2 distinct qualifying insiders, 30-day window → monitor only, do not act',
        'CLUSTER_STRONG: 3+ distinct qualifying insiders, 30-day window, combined value >$150,000 → D3 acts',
        'CLUSTER_ELITE:  5+ distinct qualifying insiders OR C-suite cluster (CEO+CFO or CEO+COO) buying, combined >$500,000',
        'CLUSTER_BIOTECH: ANY qualifying insider buy within 60 days of known catalyst date (Phase 2/3 readout, FDA PDUFA date, NDA filing) → separate elevated conviction tier',
        '"Distinct" = different named individuals. CEO buying 3 times = 1 insider.',
        'NOTE: small-cap biotechs — lower the value threshold to $10,000 minimum (shares may be $2-5 each)',
        '',
        '── STEP 3: BIOTECH-SPECIFIC RULES (separate logic path) ─────────────',
        'Biotech trigger: company has a known catalyst date in next 30-90 days (FDA PDUFA, Phase 2/3 data readout, NDA decision)',
        'ANY qualifying executive buy within 60 days of catalyst = CLUSTER_BIOTECH regardless of cluster count',
        'Why: biotech executives who buy before catalyst have read their own trial data. This is the strongest single signal in biotech.',
        'Biotech position size: SMALLER than normal — 0.5-1% max. Binary outcomes = all-or-nothing risk.',
        'Biotech stop: -15% (wider — biotech is volatile). Exit before catalyst if position is profitable (+50%).',
        'VETO biotech cluster if: company has announced a registered direct offering in last 30 days (dilution incoming)',
        'VETO biotech cluster if: the insider buying is coming from only board members (executives know trial data, board may not)',
        'Positive biotech signal: CEO + CMO (Chief Medical Officer) both buy within 30 days pre-catalyst = CLUSTER_BIOTECH_ELITE',
        '',
        '── STEP 4: PRE-EARNINGS / PRE-RESULTS CLUSTER ───────────────────────',
        'When: cluster fires within 60 days before quarterly (10-Q) or annual (10-K) earnings report',
        'Thesis: insiders buying before results = they expect a beat or positive guidance update',
        'Enhanced conviction: cluster + stock near 52W low + analyst consensus below last quarters actual',
        'Position: CLUSTER_STRONG pre-earnings = 1.5% (smaller — hold through print requires wider stop)',
        'Exit options: (A) exit before earnings print (+take whatever gain), (B) hold through print with wider stop',
        'VETO pre-earnings cluster if: CFO is one of the sellers in last 90 days (CFO knows exact numbers)',
        '',
        '── STEP 5: INSIDER SELLING AS VETO / SHORT SIGNAL ───────────────────',
        'Single insider sale of >50% of their stake = strong negative signal. VETO any long. Consider short.',
        'Multiple insiders selling in same 30-day window = DISTRIBUTION. Flag for Scenario I review.',
        'CEO/CFO selling within 30 days of your cluster entry → EXIT IMMEDIATELY (hard rule)',
        'Exception: insider selling <5% of stake for diversification after multi-year hold = not bearish',
        '',
        '── ENTRY AND SIZING ─────────────────────────────────────────────────',
        'Trigger: qualifying 3rd distinct purchase filed (CLUSTER_STRONG) or any qualifying buy near catalyst (CLUSTER_BIOTECH)',
        'Entry: limit order at last qualifying insider purchase price OR next-day market open',
        'Veto entry if: stock already >20% above the first qualifying cluster purchase (price ran — entry too late)',
        'Size: CLUSTER_STRONG = 2% · CLUSTER_ELITE = 3% · CLUSTER_BIOTECH = 0.5-1% · CLUSTER_BIOTECH_ELITE = 1.5%',
        'Scale: 50% day 1, 25% day 2, 25% day 3 — never put full position on immediately',
        '',
        '── STOP AND EXIT ─────────────────────────────────────────────────────',
        'Standard stop: -8% from entry (insider timing is imprecise — give it room)',
        'Biotech stop: -15% from entry (binary event risk)',
        'Hard exit trigger: any qualifying cluster insider files a SALE within 30 days of entry → exit 100%',
        'Profit exit: take 50% at +20%, trail remaining 50% with 10% trailing stop',
        'Time stop: if no progress after 60 days → exit (thesis may be wrong on timing)',
        '',
        '── COMBINED SIGNALS ──────────────────────────────────────────────────',
        'CLUSTER_STRONG + Scenario F Mode 1 (institutional accumulation) = maximum long conviction',
        'CLUSTER_STRONG + Scenario H (consolidation breakout) = entry timing confirmed by technicals',
        'CLUSTER_BIOTECH_ELITE + FDA PDUFA date within 30 days = highest risk/reward biotech trade',
        'CLUSTER_STRONG + stock at 52W low + RSI < 35 = multi-factor extreme undervaluation signal',
        'Any 3-scenario convergence → ULTRA_HIGH_CONFIDENCE tag in SignalLog',
      ],
      legalNote: 'Reading public Form 4 EDGAR filings is LEGAL. Trading on private insider information is securities fraud. D3 uses only public filings. Biotech catalyst dates are public (FDA calendar). Never trade on trial data received privately.',
      prereqs: [
        'EDGAR Form 4 parser built (Phase C AI Scorecard session)',
        'FDA PDUFA calendar feed integrated (free: FDA.gov calendar)',
        'D3a shadow mode complete',
      ],
      doD: [
        'Form 4 parser filtering: only transaction_code=P + value >=$25k + not prescheduled',
        'Cluster detection: CLUSTER_WEAK / CLUSTER_STRONG / CLUSTER_ELITE / CLUSTER_BIOTECH / CLUSTER_BIOTECH_ELITE',
        'Biotech path: separate logic checking FDA calendar for catalyst dates',
        'Pre-earnings path: cluster near 10-Q/10-K filing date detection',
        'Insider sale monitor → hard exit trigger',
        'PR-buy filter active (discard patterns documented above)',
        'sessions.js: scenario-g-insider-cluster → done',
      ],
      smokeChecks: [
        'grep -qE "CLUSTER_STRONG|CLUSTER_BIOTECH|CLUSTER_ELITE" backend/services/strategy.js 2>/dev/null && echo "Scenario G exists" || echo "MISSING"',
        'grep -qE "PR_BUY|pr_buy|minPurchaseValue|pdufa|PDUFA" backend/services/insiderParser.js 2>/dev/null && echo "Filters exist" || echo "MISSING filters"',
        'test -f backend/services/insiderParser.js && echo "insiderParser exists" || echo "MISSING"',
      ],
      files: ['backend/services/strategy.js', 'backend/services/insiderParser.js', 'backend/services/fdaCalendar.js'],
      vendorDeps: [
        { name: 'SEC EDGAR', plan: 'Free', endpoint: 'Form 4 full-text search', status: 'ACTIVE' },
        { name: 'AInvest API', plan: 'Free tier', endpoint: '/open/ownership/insider', status: 'EVALUATING backup' },
      ],
    },

  {
      id: 'scenario-h-consolidation-breakout',
      sessionNumber: '02cd4d7',
      name: 'Scenario H — Post-Catalyst Consolidation Breakout (Highest Probability Setup)',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'Stock gaps on strong catalyst, consolidates 10-35 days with volume drying up (weak hands sold), then breaks out on returning volume. The tightest stop of all 8 strategies = best risk/reward ratio. Works long AND short (inverse breakdown pattern).',
      items: [
        '── 4 OBJECTIVE STAGES (all must confirm) ────────────────────────────',
        'Stage 1 — CATALYST: gap >5% on fundamental news, volume >3x average',
        'Stage 2 — CONSOLIDATION: sideways ±3% from gap close for 10-35 calendar days',
        'Stage 3 — VOLUME DRY-UP: daily volume < 50% of 20-day average (KEY TELL: weak hands sold)',
        'Stage 4 — BREAKOUT: closes above consolidation high on volume >1.5x average → ENTRY',
        '── ENTRY AND SIZING ─────────────────────────────────────────────────',
        'Confirm volume on breakout day BEFORE entering. Volume < 1.5x average = false breakout, wait.',
        'False breakout: if stock closes back below consolidation high on same day → no entry.',
        'Entry: next day LIMIT order at breakout close price. Do not chase intraday.',
        'Position: 1.5-2.5%. Scale: 70% on breakout, 30% on successful retest of breakout level.',
        '── STOPS AND TARGETS ────────────────────────────────────────────────',
        'Stop: below the consolidation LOW (tightest stop of all 8 strategies)',
        'Target 1 (+15%): take 40%. Target 2 (+25%): take 40%. Trail remaining 20% with 10% trailing stop.',
        'Time stop: if no progress in 30 calendar days → exit (opportunity cost)',
        '── SHORT VARIANT (inverse pattern) ─────────────────────────────────',
        'Stage 1 SHORT: drops >5% on bad catalyst. Stage 2+3 same (consolidation above breakdown close).',
        'Stage 4 SHORT: closes BELOW consolidation low on volume >1.5x average → entry short.',
        'Same sizing and stops (inverted). D3 tag: CONSOLIDATION_BREAKOUT',
        '── VETO CONDITIONS ──────────────────────────────────────────────────',
        'VETO if: consolidation range > ±8% (too volatile, pattern not clean)',
        'VETO if: VIX > 35 (REGIME_EXTREME — breakouts fail in crisis)',
        'VETO if: earnings within 10 days of breakout signal',
        'VETO if: sector ETF making new lows while stock consolidates',
        '── COMBINED SIGNAL (multi-scenario convergence) ─────────────────────',
        'H + F Mode 2 (squeeze trigger) + G CLUSTER_STRONG = ULTRA_HIGH_CONFIDENCE tag',
      ],
      d3Value: 'Objective 4-stage pattern. Tightest stop = best risk/reward. Clean algorithmic detection. Works in all market conditions long and short.',
      prereqs: ['D3a shadow mode complete', 'Volume data available (Tiingo EOD + AV intraday)'],
      doD: [
        'All 4 stage detector in strategy.js',
        'Volume dry-up detection (<50% 20-day avg)',
        'Breakout confirmation volume check',
        'Long AND short variant active',
        'Multi-scenario ULTRA_HIGH_CONFIDENCE tagging',
        'sessions.js: scenario-h-consolidation-breakout → done',
      ],
      smokeChecks: [
        'grep -qE "CONSOLIDATION_BREAKOUT|volumeDryUp" backend/services/strategy.js 2>/dev/null && echo "Scenario H exists" || echo "MISSING"',
      ],
      files: ['backend/services/strategy.js', 'backend/services/tradeGate.js'],
      vendorDeps: [
        { name: 'Tiingo', plan: 'Internal', endpoint: 'EOD volume for pattern detection', status: 'ACTIVE' },
        { name: 'Alpha Vantage', plan: 'Free intraday', endpoint: 'Intraday volume confirmation', status: 'ACTIVE' },
      ],
    },

  {
      id: 'scenario-i-fraud-investigation',
      sessionNumber: '02cd4d7',
      name: 'Scenario I — Accounting Fraud / SEC Investigation / Short Seller Report / Late Filing',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'CVNA-type setups. When accounting irregularities, SEC investigations, prominent short seller reports, or unexplained filing delays emerge — the stock often has a predictable multi-day to multi-week decline. Can also identify the reversal if allegations are later found to be overblown. Two directions: SHORT (primary, fraud confirmed) and LONG (secondary, overblown allegations recovering).',
      items: [
        '── THE 4 TRIGGER EVENTS ─────────────────────────────────────────────',
        '',
        'TRIGGER 1: PROMINENT SHORT SELLER REPORT',
        'Sources to monitor: Hindenburg Research, Citron Research, Muddy Waters, Gotham City, White Diamond',
        'When published: stock typically drops 15-30% on day 1. The opportunity is NOT day 1 (too volatile).',
        'D3 strategy: wait 2-3 trading days for initial panic to settle → assess rebound quality → SHORT the dead-cat bounce',
        'Dead-cat bounce tells: stock recovers 30-50% of initial drop on LOW volume = weak hands covering, not genuine recovery',
        'Short entry: on 2nd or 3rd day when bounce fails on declining volume (Scenario H inverse pattern)',
        'Why this works: most Hindenburg/Muddy Waters reports are researched 12-18 months. The allegations rarely go away.',
        'Size: 1-1.5% (short seller reports can be wrong — occasionally the target fights back and wins)',
        '',
        'TRIGGER 2: SEC INVESTIGATION OR SUBPOENA DISCLOSED',
        'Source: 8-K filing on EDGAR — mandatory disclosure of formal SEC investigation or Wells Notice',
        'Wells Notice = SEC has made a preliminary determination to recommend enforcement action = very serious',
        'A "formal order of investigation" is less serious than a Wells Notice but still bearish',
        'Formal SEC investigation: stock typically continues declining for weeks as details emerge',
        'D3 strategy: SHORT on the 8-K disclosure day (confirmation is in the filing — not rumor)',
        'Size: 1.5-2% — SEC investigations are lengthy and create sustained downward pressure',
        'Cover: (A) when company announces settlement terms (temporary bounce), (B) at -25% from entry',
        'VETO short if: company simultaneously announces a strategic review or buyout interest (M&A can rescue)',
        '',
        'TRIGGER 3: LATE 10-K OR 10-Q FILING (with no explanation)',
        'SEC deadlines: Large accelerated filer 10-K = 60 days after FY end. 10-Q = 40 days after quarter end.',
        'An NT filing (Form NT 10-K or NT 10-Q) = notification of late filing. MANDATORY to disclose reason.',
        'NT filing with vague reason (needs more time, auditor review) = VERY BEARISH SIGNAL',
        'Why: legitimate companies that are fine file on time. Late + vague = auditor has found something.',
        'Common patterns that follow late filing: restatement, revenue recognition problem, internal control weakness',
        'Historical precedent: Enron, WorldCom, Wirecard, CVNA all showed filing irregularities before collapse',
        'D3 strategy: SHORT on NT filing with vague reason OR when filing is missed without NT filed at all',
        'Size: 1% initial. Add 0.5% if auditor subsequently issues going concern or resigns',
        'Cover: before the actual filing date (if they eventually file, stock may bounce)',
        'TRIGGER VETO: if NT filing reason is clearly operational (hurricane, system migration) = not bearish',
        '',
        'TRIGGER 4: ACCOUNTING IRREGULARITY INDICATORS (proactive detection)',
        'These are detected by D3 from SEC filings BEFORE anyone reports on them:',
        'Revenue recognition: quarter-over-quarter revenue growth > 40% while AR (accounts receivable) grows > 60% = channel stuffing flag',
        'Cash flow divergence: net income growing but operating cash flow declining for 3+ quarters = earnings quality problem',
        'Auditor change: company switches Big 4 auditor without clear business reason = concern',
        'Auditor resignation: any auditor resignation = IMMEDIATE bearish signal. File an 8-K within 4 days.',
        'Goodwill impairment: large unexpected goodwill write-down = management admitted prior acquisition was overvalued',
        'Related party transactions: large payments to CEO-controlled entities in 10-K footnotes = governance risk',
        'D3 action: flag the ticker as ACCOUNTING_REVIEW. Add to monitoring universe. Do not short yet — wait for confirmation.',
        'Confirmation needed: short seller report OR SEC filing OR analyst downgrade citing the same metric',
        '',
        '── LONG VARIANT: OVERBLOWN ALLEGATIONS RECOVERY ─────────────────────',
        'Occasionally a short seller report is wrong, exaggerated, or the company successfully refutes it.',
        'Signals of overblown allegations: company provides detailed rebuttal within 5 days, CEO buys shares immediately, independent board review finds nothing, stock stabilises and builds base above short seller target price',
        'D3 long entry: if stock held above 52W low + insider buying + refutation is specific and audited',
        'Long size: 0.5-1% only. This is SPECULATIVE. Most companies that fight back still decline.',
        'Hard stop: below the low of the first panic day. If that breaks, the report was likely correct.',
        '',
        '── VETO CONDITIONS ──────────────────────────────────────────────────',
        'VETO all shorts if: company is in active M&A process (acquirer due diligence reduces fraud risk)',
        'VETO all shorts if: management has already hired forensic auditor and disclosed it (cleaning house)',
        'VETO if: short seller has been wrong on this company before (check their track record)',
        'VETO if: VIX > 35 (REGIME_EXTREME) — do not short in crisis — everything falls, no edge',
        '',
        '── POSITION SIZING AND RISK MANAGEMENT ──────────────────────────────',
        'Short seller report: 1-1.5% · SEC investigation: 1.5-2% · Late filing: 1% · Accounting flag: 0.5% (monitor only)',
        'NEVER short a company solely on social media rumor. Require documented SEC filing or named research report.',
        'Short selling specific risk: if company announces buyout or settlement → stock can gap up 50%+ → strict stop mandatory',
        'Stop all shorts: +10% above entry (if market disagrees with thesis, exit fast)',
        '',
        '── DATA SOURCES ──────────────────────────────────────────────────────',
        'EDGAR 8-K monitor: watch for "Wells Notice", "formal order of investigation", "NT 10-K", "NT 10-Q", "auditor resigned"',
        'Short seller RSS feeds: Hindenburg Research (hindenburgresearch.com/feed), Muddy Waters, Citron — scrape daily',
        'EDGAR XBRL data: revenue + AR growth rates, operating cash flow vs net income divergence (free, quarterly)',
        'FDA calendar (for biotech fraud detection): if biotech CEO sells before FDA rejection = insider trading flag',
        '',
        '── LEGAL BOUNDARIES ─────────────────────────────────────────────────',
        'Short selling stocks is LEGAL.',
        'Using publicly disclosed SEC filings, research reports, and EDGAR data is LEGAL.',
        'Never short based on non-public information. Never coordinate with short sellers. Never spread information.',
        'D3 is a passive reader of public information — the edge is systematic monitoring, not information advantage.',
        'If D3 detects probable fraud BEFORE it is public — DO NOT TRADE. Wait for public disclosure. Trading on non-public info = illegal.',
      ],
      d3Value: 'Accounting fraud and SEC investigations create sustained multi-week short opportunities. Late filings and auditor changes are EDGAR-detectable before the market reacts. Proactive monitoring = earlier entry than reactive traders.',
      legalNote: 'Short selling is legal. Using public EDGAR filings and named research reports is legal. Never trade on non-public information about a company regardless of how it was obtained.',
      prereqs: [
        'D3a shadow mode complete',
        'EDGAR 8-K parser watching for key phrases (Wells Notice, NT 10-K, auditor resigned)',
        'Short seller RSS feed monitor running',
        'Scenario F Mode 3 (contrarian short) infrastructure available (broker short availability check)',
      ],
      doD: [
        'EDGAR 8-K keyword monitor: Wells Notice, NT 10-K, NT 10-Q, auditor resigned, formal order of investigation',
        'Short seller RSS feed scraper: Hindenburg, Muddy Waters, Citron',
        'Accounting irregularity scanner: revenue vs AR divergence, OCF vs NI divergence (quarterly XBRL)',
        'All 4 triggers logging to SignalLog with trigger_type field',
        'Long recovery variant logic active',
        'sessions.js: scenario-i-fraud-investigation → done',
      ],
      smokeChecks: [
        'grep -qE "WELLS_NOTICE|NT_FILING|SHORT_SELLER_REPORT|ACCOUNTING_REVIEW" backend/services/strategy.js 2>/dev/null && echo "Scenario I exists" || echo "MISSING"',
        'test -f backend/services/fraudDetector.js && echo "fraudDetector exists" || echo "MISSING"',
        'test -f backend/services/edgarMonitor.js && echo "edgarMonitor exists" || echo "MISSING"',
      ],
      files: ['backend/services/strategy.js', 'backend/services/fraudDetector.js', 'backend/services/edgarMonitor.js'],
      vendorDeps: [
        { name: 'SEC EDGAR', plan: 'Free public domain', endpoint: '8-K full-text search + XBRL financial data', status: 'ACTIVE — no key needed' },
        { name: 'Hindenburg Research RSS', plan: 'Free', endpoint: 'hindenburgresearch.com/feed', status: 'SCRAPE daily' },
        { name: 'Muddy Waters RSS', plan: 'Free', endpoint: 'muddywatersresearch.com/feed', status: 'SCRAPE daily' },
      ],
    },

  {
      id: 'emergency-stop',
      sessionNumber: '02cd4d7',
      name: 'Emergency Stop — Phone-Accessible Kill Switch for D3',
      status: 'planned',
      phase: 'Phase 10 — D3 Paper',
      description: 'MANDATORY before D3c (real money). D3 runs 24/7 while owner is at work. A flash crash, runaway position, or system error needs to be stoppable from a phone in under 30 seconds. One authenticated endpoint that immediately closes all positions and cancels all orders.',
      items: [
        '── THE ENDPOINT ──────────────────────────────────────────────────────',
        'POST /api/trading/emergency-stop — Super Admin JWT required',
        'Action sequence: (1) call ib.cancelAllOrders() (2) call ib.closeAllPositions() via market orders (3) set D3_ENABLED=false in Redis (4) email confirmation to owner (5) log to EmergencyStopLog',
        'Response: JSON { stopped: true, positions_closed: N, orders_cancelled: N, timestamp }',
        'No UI needed beyond a browser bookmark on the phone: [POST https://api.marketpulze.com/api/trading/emergency-stop]',
        '── THE PHONE INTERFACE ───────────────────────────────────────────────',
        'Simple HTML page at /trading/emergency (Super Admin only — no JS framework needed)',
        'Single red button: "STOP ALL POSITIONS NOW"',
        'Confirmation: shows positions closed + orders cancelled + timestamp',
        'Must work on mobile Safari and Chrome — no complex React needed for this page',
        'Add to phone home screen as a PWA shortcut for instant access from lock screen',
        '── RESTART PROCEDURE ────────────────────────────────────────────────',
        'After emergency stop: D3 stays halted (D3_ENABLED=false in Redis)',
        'Owner must manually review open positions, reconcile, then POST /api/trading/d3-resume',
        '/api/trading/d3-resume: sets D3_ENABLED=true, sends confirmation email, logs restart',
        '── TESTING REQUIREMENT ──────────────────────────────────────────────',
        'Test in paper trading mode BEFORE D3c. Verify: stops fire, positions close, email received, D3 stays halted',
        'Monthly drill: trigger emergency stop on paper account. Verify full sequence. Log result.',
      ],
      prereqs: ['D3b: Alpaca paper trading running', 'SMTP2GO email confirmed working', 'Super Admin auth confirmed on mobile'],
      doD: [
        'POST /api/trading/emergency-stop working and tested',
        '/trading/emergency mobile page live (Super Admin only)',
        'Phone home screen shortcut documented in HANDOVER',
        'Email confirmation tested and received within 60 seconds',
        'Emergency stop tested on paper account — verified positions close',
        'sessions.js: emergency-stop → done',
      ],
      smokeChecks: [
        'grep -q "emergency-stop" backend/routes/trading.js 2>/dev/null && echo "Emergency stop route exists" || echo "MISSING emergency stop"',
      ],
      files: ['backend/routes/trading.js', 'src/pages/EmergencyStopPage.jsx'],
      vendorDeps: [],
    },


  // ─── PHASE D3c — D3 CAPITAL DEPLOY ─────────────────────────────────────

  {
      id: 'D3c',
      sessionNumber: '02cd4d7',
      name: 'D3c — Live Micro Trading (IBKR Live, Strict Capital Limits)',
      status: 'future',
      phase: 'Phase 11 — D3 Capital Deploy',
      items: [
        '★ First real money — IBKR live account. Start 5% of intended capital. $500/day loss hard cap.',
        '★ Run minimum 90 days before advancing to D3d.',
        'Switch broker.js from Alpaca paper to IBKR Client Portal API (IB Gateway port 4001)',
        'Strategy 1 active: Institutional Footprint/Momentum (Scenario B) — highest confidence, most validated in D3b',
        'Strategy 2 active: Spring Effect/Mean Reversion (Scenario C) — add after 30 days live if Strategy 1 stable',
        'After-Hours Intelligence Module: monitor AH volume spikes > 500% of normal AH average without price move → Institutional Absorption flag',
        'Overnight Gap Predictor: AH Institutional Absorption + AH volume spike → pre-market limit order at 7am ET',
        'Lying AH Move detector: stock up 10% AH on < 5000 shares + spread > 2% → Low Liquidity Trap → VETO open',
        'Dark Pool Print scanner: large block trades appearing at 4:01 PM at price different from close → strong trend day signal',
        'HFT Spoofing detection: Level 2 order > $100k appears 2% below price then cancels when approached → Fake Support → raise veto sensitivity',
        'PDT rule management: if margin account < $25k max 3 day-trades per 5 business days. Mitigation: use cash account or ensure > $25k.',
        'Daily Post-Mortem: Claude writes 1-page structured report at 4:30 PM — wins, losses, vetoed trades, weight adjustment suggestions',
        'Manual approval required for any weight adjustment suggestion from Post-Mortem — never auto-apply',
      ],
      prereqs: [
        'D3b complete: 60+ days paper, Sharpe > 0.8, max drawdown did not exceed 10%',
        'IBKR live account funded. PDT rule understood and managed.',
        'IB Gateway confirmed stable on OCI VM (no disconnections in 7 days)',
      ],
      doD: [
        'IBKR live executing real trades with bracket orders',
        'Strategy 1 (Momentum) profitable in isolation over 30+ days',
        'Strategy 2 (Mean Reversion) added and validated after 30 days',
        'After-hours intelligence module running, gap predictions logged and compared to actuals',
        'HFT spoofing detection active in Level 2 stream',
        'Post-Mortem reports generated daily, archived in MongoDB',
        '90 days minimum. Live Sharpe > 0.8. Max drawdown < 10%.',
        'sessions.js: D3c → done, D3d → next',
      ],
      smokeChecks: [
        'grep -qE "IBKR_CLIENT_PORTAL|ibkr.*live|port.*4001" backend/services/broker.js 2>/dev/null && echo "IBKR live config" || echo "MISSING"',
        'grep -qE "afterHours|overnight_gap|dark_pool" backend/services/strategy.js 2>/dev/null && echo "AH module exists" || echo "MISSING"',
        'grep -qE "postMortem|post_mortem|4:30|16:30" backend/services/strategy.js 2>/dev/null && echo "PostMortem exists" || echo "MISSING"',
      ],
      files: ['HANDOVER.md', 'src/data/sessions.js'],
      vendorDeps: [
        { name: 'IBKR Live', plan: 'Client Portal API (free with account)', endpoint: 'IB Gateway port 4001 on OCI', status: 'D3c only — never before' },
      ],
    },

  {
      id: 'regime-aware-signal-weights',
      sessionNumber: '02cd4d7',
      name: 'Regime-Aware Signal Weights — VIX-Conditional Performance Tracking',
      status: 'planned',
      phase: 'Phase 11 — D3 Capital Deploy',
      description: 'A momentum signal that performs well in VIX < 15 trending markets fails in VIX 25-35 choppy markets. The self-learning loop must track signal accuracy PER REGIME — not globally. Without this, a regime change causes a run of losses that gets incorrectly attributed to a broken signal.',
      items: [
        '── REGIME DEFINITIONS (VIX-based for simplicity) ─────────────────────',
        'REGIME_LOW_VOL:    VIX < 15 — trending bull/bear. Momentum strategy thrives.',
        'REGIME_NORMAL:     VIX 15-25 — mixed. Most strategies work with normal sizing.',
        'REGIME_HIGH_VOL:   VIX 25-35 — choppy/fearful. Reduce size 50%, mean reversion preferred.',
        'REGIME_EXTREME:    VIX > 35 — crisis mode. Cash only or inverse ETFs. No momentum.',
        '── SIGNAL WEIGHT STORAGE ────────────────────────────────────────────',
        'Each signal in SignalLog stores: signal_name, outcome, market_regime (at time of trade)',
        'Weekly analysis: signal accuracy BY REGIME. 4 separate leaderboards.',
        'Weight adjustment: only apply regime-specific weight change within that regime. Do not pollute others.',
        '── REGIME DETECTION ─────────────────────────────────────────────────',
        'Current regime: computed from live VIX (VIXY ETF proxy) at market open',
        'Regime change: if VIX crosses threshold → log regime_change_event → notify owner',
        'D3 strategy selection by regime: LOW_VOL → all strategies. NORMAL → all minus pump. HIGH_VOL → mean reversion + value only. EXTREME → ABORT all.',
        '── SELF-CALIBRATING REGIME DETECTION (Phase D3d) ────────────────────',
        'Track: does VIX threshold of 25 correctly predict strategy performance? Or is 20 better for this portfolio?',
        'Monthly: compare regime predictions vs actual signal accuracy. Adjust VIX thresholds if data supports it.',
      ],
      d3Value: 'Prevents regime-mismatch losses from being misattributed to broken signals. Makes self-learning loop regime-aware.',
      prereqs: ['D3c: live trading 90+ days with SignalLog capturing regime at trade time'],
      doD: [
        'Regime field added to SignalLog and CounterfactualLog',
        'Regime detection running at market open',
        'Weekly analysis shows 4 separate signal leaderboards (one per regime)',
        'Weight adjustments are regime-scoped',
        'sessions.js: regime-aware-signal-weights → done',
      ],
      smokeChecks: [],
      files: ['backend/models/SignalLog.js', 'backend/services/strategy.js'],
      vendorDeps: [],
    },


  // ─── PHASE D3d — D3 SCALE ───────────────────────────────────────────────

  {
      id: 'D3d',
      sessionNumber: '02cd4d7',
      name: 'D3d — Full Autonomous Expansion',
      status: 'future',
      phase: 'Phase 11 — D3 Capital Deploy',
      items: [
        '★ Expand Kelly multiplier from 0.5 to full Half-Kelly as win rate stabilises',
        'Strategy 3 active: Sector Sympathy/Arbitrage (Scenario D) — correlation matrix tracking vs sector ETFs',
        'Strategy 4 active: Inorganic Hype/Promotion Detection (Scenario A) — Sentiment Velocity engine + SEC filing cross-check',
        'Market Regime Awareness: simple VIX threshold regime (Phase 1) — Low-Vol Bull / High-Vol Bear / Sideways / Breakout',
        'In Sideways regime: reduce all position sizes by 70%, Scenario B/D only, no Scenario A/C',
        'In High-Vol Bear: switch to Mean Reversion (Scenario C) shorts or cash only',
        'Recursive Backtesting: before entry, run 60-second backtest on last 48 hours to confirm pattern is currently working',
        'Capital Allocation AI: best-performing strategy gets 40% of trading capital, second gets 25%, rest split',
        'Strategy Health Monitor: rolling Sharpe per strategy. If Sharpe < 0.8 over 30 days → disable strategy automatically',
        'Options signals display in /trading/mdd — display only, NO automated options execution (regulatory complexity)',
        'NOTE: HMM regime detection, Reinforcement Learning, vector databases, LangGraph/AutoGen — D4+ territory, not this session',
      ],
      prereqs: [
        'D3c complete: 90+ days live, live Sharpe > 0.8, max drawdown < 10%',
        'At least 2 strategies individually profitable over 30+ days',
        'Post-Mortem weight adjustments reviewed and approved over several weeks',
      ],
      doD: [
        'All 4 trading scenarios active and individually monitored',
        'Market regime detection switching strategy behaviour automatically',
        'Capital Allocation AI distributing capital by strategy performance',
        'Strategy Health Monitor disabling underperforming strategies automatically',
        'Full Kelly expansion gradual: 0.1 → 0.25 → 0.5 over 3 months as win rate stabilises',
        'sessions.js: D3d → done',
      ],
      smokeChecks: [],
      files: ['HANDOVER.md', 'src/data/sessions.js'],
      vendorDeps: [],
    },


  // ─── PHASE D — ZENITH + IBKR ────────────────────────────────────────────

  {
      id: 'D0',
      sessionNumber: '02cd4d7',
      name: 'Personal Trading Suite (/trading)',
      status: 'future',
      phase: 'Phase 8 — D3 Foundation',
      items: [
        '★ Super Admin only — completely hidden from all other users',
        '/trading/journal — personal trade log: entry/exit/P&L/win rate/strategy tags',
        '/trading/watchlist — pro watchlist with RSI/float/short%/signal columns',
        '/trading/alerts — custom alert builder: price/volume/signal/keyword',
        '/trading/scanner — personal saved scan configurations (private Scanz)',
        '/trading/signals — signal accuracy vs actual outcomes',
        '/trading/premarket — 5am daily brief: futures + movers + earnings',
        '/trading/smartmoney — combined insider + congressional dashboard',
      ],
      prereqs: ['Phase C complete', 'OCI migration complete'],
      doD: [], smokeChecks: [], files: [], vendorDeps: [],
    },

  {
      id: 'performance-dashboard',
      sessionNumber: '02cd4d7',
      name: 'Performance Dashboard — Manual vs D3 Side-by-Side Proof',
      status: 'planned',
      phase: 'Phase 8 — D3 Foundation',
      description: 'CORE DELIVERABLE. /trading/performance — Super Admin only. Side-by-side comparison of owner manual trading vs D3 systematic trading. This dashboard PROVES D3 value before live capital is committed. Shows exactly where emotion costs money and where discipline beats intuition.',
      items: [
        '/trading/performance — Super Admin route, hidden from all other users',
        'Manual trade journal: ticker, date, entry, exit, size, strategy tag, reasoning, emotion flag (panic/FOMO/revenge/conviction)',
        'D3 shadow log: what D3 would have done on same day (D0 phase — logs without executing)',
        'P&L by strategy independently: momentum / speculation / value / short / pump — each tracked separately',
        'Combined metrics: win rate, avg win, avg loss, max drawdown, Sharpe ratio — per strategy AND combined',
        'Comparison views: weekly / monthly / quarterly / yearly — manual vs D3 side by side',
        'Divergence analysis: where did manual and D3 disagree? Who was right? 60-day rolling window',
        'Emotion cost calculator: when owner overrode D3, what was the cost? Aggregated over time.',
        '24/7 activity timeline: what D3 detected and decided while owner was at work',
        'Self-improvement log: signal weights changed → expected vs actual result 30 days later',
        'Loss classification: wrong signal / wrong execution / external factor / correct but bad luck',
        'This dashboard IS the evidence required before committing real capital to D3c',
      ],
      d3Value: 'Proves D3 earns live capital. Emotion cost tracker is unique — no platform has this feature.',
      prereqs: [
        'D0 Personal Trading Suite live and actively used for 60+ days',
        'SignalLog running and capturing real decisions',
      ],
      doD: [
        '/trading/performance route live (Super Admin only)',
        'Manual trade journal input working',
        'D3 shadow log displaying vs manual trades',
        'All 5 strategy tracks displaying independently',
        'Emotion cost calculator calculating correctly over real data',
        'sessions.js: performance-dashboard → done',
      ],
      smokeChecks: [],
      files: ['src/pages/RoadmapPage.jsx', 'backend/routes/trading.js', 'backend/models/TradeJournal.js'],
      vendorDeps: [],
    },

  {
      id: 'D3-Infra',
      sessionNumber: '02cd4d7',
      name: 'D3 Infrastructure Setup — OCI + IBKR Gateway + Redis',
      status: 'future',
      phase: 'Phase 8 — D3 Foundation',
      items: [
        '★ Prerequisite for ALL D3 sessions — nothing else in D3 starts without this',
        'OCI VM-1 live ✅ (May 4, 2026) · Railway decommissioned ✅ · backend at api.marketpulze.com',
        'Install IBKR IB Gateway (Java process, port 4001) on OCI VM — runs 24/7 persistently',
        'NOTE: Use port 4001 (IB Gateway) NOT port 7497 (TWS desktop) — Gateway is for automated use',
        'Install ib_insync Python library on OCI VM — connects to IB Gateway via socket',
        'Confirm handshake: ib.connect("127.0.0.1", 4001, clientId=1) returns connected=True',
        'Install Redis on OCI VM (self-hosted) — real-time tick cache and order state store',
        'PM2 process manager: configure auto-restart for IB Gateway + D3 trading engine',
        'Security: IBKR credentials in OCI environment variables — never in code',
        'Add IBKR Market Data Subscriptions inside IBKR account: NASDAQ TotalView + NYSE OpenBook (~$15-30/mo)',
        'This Level 2 data is required for Order Book Imbalance (OBI) calculation and HFT spoofing detection',
        'Heartbeat monitor: if IB Gateway disconnects → send email alert via SMTP2GO → pause all D3 logic',
        'Env vars to add to OCI (NOT before this session): ALPACA_API_KEY, ALPACA_SECRET_KEY, ALPACA_BASE_URL',
      ],
      prereqs: [
        'OCI VM-1 live ✅ · Railway decommissioned ✅ (May 4, 2026) · api.marketpulze.com is primary',
        'IBKR Pro account active with funded paper trading account',
        'D0 Personal Trading Suite: live and used manually for minimum 60 days',
        'Manual trading signals in D0 show positive Sharpe ratio (positive expectancy validated)',
        'Phase C complete: Screener (S27D) + AI Scorecard (S28) + insider/institutional data live',
      ],
      doD: [
        'IB Gateway running on OCI VM on port 4001, survives reboot (PM2 startup)',
        'ib_insync handshake confirmed: ib.connect() returns connected=True',
        'Redis running on OCI VM, accessible from trading engine',
        'Heartbeat monitor running: disconnection sends email alert within 60 seconds',
        'IBKR Level 2 market data subscription active and streaming',
        'All env vars set on OCI — zero secrets in code',
        'sessions.js: D3-Infra → done, D3a → next',
      ],
      smokeChecks: [
        'test -f backend/services/broker.js && echo "broker service exists" || echo "MISSING broker service"',
        'test -f backend/services/tradeGate.js && echo "tradeGate exists" || echo "MISSING tradeGate"',
        'test -f backend/services/riskManager.js && echo "riskManager exists" || echo "MISSING riskManager"',
      ],
      files: ['HANDOVER.md', 'src/data/sessions.js'],
      vendorDeps: [
        { name: 'IBKR IB Gateway', plan: 'Free with IBKR Pro account', endpoint: '127.0.0.1:4001', status: 'Install on OCI VM' },
        { name: 'Alpaca', plan: 'Free paper trading', endpoint: 'paper-api.alpaca.markets', status: 'Add env vars at D3a start only' },
        { name: 'Redis', plan: 'Self-hosted on OCI VM', endpoint: 'localhost:6379', status: 'Install on OCI VM' },
        { name: 'IBKR Market Data', plan: '~$15-30/mo', endpoint: 'NASDAQ TotalView + NYSE OpenBook', status: 'Subscribe inside IBKR account' },
      ],
    },

  {
      id: 'D2',
      sessionNumber: '02cd4d7',
      name: 'Phase D Unique Features',
      status: 'future',
      phase: 'Phase 11 — D3 Capital Deploy',
      items: [
        '★ ETF Overlap Detector — watchlist exposure vs QQQ/SPY',
        '★ Portfolio Stress Test — S&P -20% estimated watchlist impact via beta',
        '★ Sector Rotation Tracker — Sankey diagram of institutional flow',
        '★ Earnings Call AI Summarizer — Claude reads FMP transcripts',
        '★ AI Portfolio Builder — risk/sector input → 10-stock watchlist with reasoning',
      ],
      prereqs: ['Phase C complete', 'Monetisation active'],
      doD: [], smokeChecks: [], files: [], vendorDeps: [],
    },

  {
      id: 'D',
      sessionNumber: '02cd4d7',
      name: 'Zenith Scanner — Phase D Infrastructure',
      status: 'future',
      phase: 'Phase 11 — D3 Capital Deploy',
      items: [
        'Natural language screener ("NASDAQ tech under $50, 3% pre-market gap")',
        'Multi-Timeframe Confluence — Harmony Score 0-100%',
        'Dark pool / block trade detection (FINRA TRF paid feed)',
        'Relative Rotation Graph (RRG) sector map',
        'REQUIRES: Apache Kafka + Redis + WebSocket — requires OCI VM-2 (Phase D · OCI-8)',
      ],
      prereqs: ['OCI migration complete', 'Subscription revenue covers infrastructure upgrade'],
      doD: [], smokeChecks: [], files: [], vendorDeps: [],
    },

  {
      id: 'cloud-fallback-aws-gcp',
      sessionNumber: '02cd4d7',
      name: 'Cloud Fallback Evaluation — AWS, GCP, Azure for D3 at Scale',
      status: 'planned',
      phase: 'Phase 11 — D3 Capital Deploy',
      description: 'OCI free → Hetzner bridge → evaluate AWS/GCP/Azure for D3 Phase D at scale. This is NOT for Phase B. Only relevant when D3 generates revenue that justifies managed cloud costs. Document decision criteria now so Phase D does not start from scratch.',
      items: [
        '── WHY THIS MATTERS AT PHASE D ────────────────────────────────────────',
        'D3d: 5 strategies running simultaneously, Redis, IB Gateway, ML backtesting — OCI 4 OCPU may become limiting',
        'AWS, GCP, Azure: better managed services, but cost increases significantly',
        '── AWS EVALUATION ──────────────────────────────────────────────────────',
        'AWS EC2 t3.medium: 2 vCPU 4GB RAM ~$30/mo. t3.large: 4GB RAM ~$60/mo.',
        'AWS advantage: Lambda for event-driven D3 triggers, RDS for structured trade data, SES for emails',
        'AWS disadvantage: complexity, cost unpredictability, over-engineered for current scale',
        'AWS free tier: 750hr/mo t2.micro (1 vCPU 1GB) — too small for IB Gateway (needs 2GB+ RAM)',
        '── GCP EVALUATION ──────────────────────────────────────────────────────',
        'GCP e2-small: 2 vCPU 2GB RAM ~$13/mo. e2-medium: 4GB RAM ~$25/mo.',
        'GCP advantage: BigQuery for trade analytics, Vertex AI for ML signal improvements',
        'GCP Always Free: e2-micro (1GB RAM) — too small. e2-small is paid.',
        'GCP free trial: $300 credit — useful for Phase D architecture exploration only',
        '── AZURE EVALUATION ─────────────────────────────────────────────────────',
        'Azure B1ms: 1 vCPU 2GB RAM ~$15/mo. B2s: 2 vCPU 4GB RAM ~$35/mo.',
        'Azure advantage: VS Code integration, GitHub Codespaces synergy, Azure OpenAI',
        '── RECOMMENDED DECISION PATH ────────────────────────────────────────────',
        'OCI free (if resolved) → Hetzner €3.79 (if blocked) → GCP e2-medium ~$25 (if D3d needs more RAM)',
        'Only move to AWS if D3 data pipeline requires Lambda + RDS + specific managed services',
        'Azure last resort — higher cost for equivalent specs',
        '── PHASE D ARCHITECTURE DECISION ───────────────────────────────────────',
        'Evaluate at D3c start (real money live). If OCI 4 OCPU shows CPU > 80% sustained → upgrade plan.',
        'Document actual resource usage at D3b (paper trading) to inform Phase D decision.',
      ],
      prereqs: ['D3b complete — resource usage benchmarked over 60 days'],
      doD: ['Cloud decision documented in HANDOVER Section 53', 'Cost model built for next 12 months'],
      smokeChecks: [],
      files: ['HANDOVER.md'],
      vendorDeps: [
        { name: 'AWS EC2', plan: 't3.medium ~$30/mo', status: 'EVALUATE at D3c' },
        { name: 'GCP Compute', plan: 'e2-medium ~$25/mo', status: 'EVALUATE at D3c' },
        { name: 'Azure VM', plan: 'B2s ~$35/mo', status: 'LAST RESORT' },
        { name: 'Hetzner CX22', plan: '€3.79/mo', status: 'PREFERRED bridge and long-term if OCI blocked' },
      ],
    },


  // ─── PHASE C/D — FUTURE EXPANSION ──────────────────────────────────────

  {
      id: '29+',
      sessionNumber: '02cd4d7',
      name: 'Crypto + Rates + Portfolio',
      status: 'future',
      phase: 'Phase 7 — Revenue Gate',
      items: [
        'Crypto tab: CoinGecko top 20 — NO signals ever, prices + % only',
        'Rates tab: FX + Gold/Oil/Yields — NO signals ever (Legal L7)',
        'Portfolio tracker — P&L informational only, no brokerage integration',
        'Side-by-side stock comparison (2-4 tickers)',
      ],
      prereqs: ['AI Scorecard (id: 28) ✅'],
      doD: [], smokeChecks: [], files: [],
      vendorDeps: [{ name: 'CoinGecko', plan: 'Free', endpoint: '/coins/markets', status: 'available when needed' }],
    },


  // ─── MONETISE ───────────────────────────────────────────────────────────

  {
      id: 'P2',
      sessionNumber: '02cd4d7',
      name: 'Stripe Monetisation',
      status: 'future',
      phase: 'Phase 7 — Revenue Gate',
      items: [
        'Stripe tiers: Free / Pro $9/mo / Premium $29/mo / Elite $49/mo',
        'subscriptionTier field already on User model ✅ — ready when needed',
        'Billing card placeholder in Admin Settings ✅',
        'Trigger: 500+ active members + 5,000+ monthly guests + Phase C complete',
        'Pro: unlimited AI, alerts, screener saves, no ads',
        'Premium: Pro + AI Scorecard + Smart Money + congressional',
      ],
      prereqs: [
        'Phase C complete (AI Scorecard live)',
        '500+ active weekly members',
        '5,000+ monthly guest visitors',
        'Signal Accuracy Tracker has 6+ months of data',
      ],
      doD: [], smokeChecks: [], files: [],
      vendorDeps: [{ name: 'Stripe', plan: '2.9% + 30¢', endpoint: 'Checkout, webhooks, portal', status: 'activate when trigger conditions met' }],
    },



  // ─── PRIORITY 4 — COMMERCIAL TRUNCATED ENGINE ───────────────────────────

  {
    id: 'd3-commercial',
    sessionNumber: 'D3-COMMERCIAL',
    name: 'D3-COMMERCIAL: Commercial Signal Product — Broker Adapter + Member Execution',
    status: 'future',
    phase: 'Phase 12 — Products 3 and 4',
    items: [
      '── WHAT THIS IS ─────────────────────────────────────────────────',
      'A commercial product where members connect their own broker account.',
      'D3 provides signals. Members execute on their own accounts.',
      'We never touch member capital. We provide the intelligence layer only.',
      'NOT Robinhood. NOT Webull. NOT a brokerage.',
      '── TRIGGER CONDITIONS (all must be true) ────────────────────────',
      'D3 inner circle validated (12+ months · positive feedback · legal reviewed)',
      'Broker adapter pattern fully tested: IBKR + ONDO minimum',
      'Legal review complete for commercial signal service',
      'Owner explicit approval to proceed',
      '── WHAT COMMERCIAL MEMBERS GET ──────────────────────────────────',
      'Signal feed: BULLISH/NEUTRAL/BEARISH with entry zone suggestion',
      'Broker connectivity: connect own IBKR or ONDO account via adapter',
      'D3 signals route to their account on their behalf',
      'Heavily rate-limited · no full engine · no L3 reasoning · no conviction formula',
      '── BROKER SUPPORT (adapter pattern — R33) ───────────────────────',
      'IBKR: IbkrAdapter (already built in D3-PRIVATE-REPO)',
      'ONDO: OndoAdapter (stub already in D3-PRIVATE-REPO)',
      'Future brokers: one new adapter file each · zero D3 engine changes',
      'Member authorises their own broker connection — we never hold their credentials',
      '── WHAT IS NEVER EXPOSED ────────────────────────────────────────',
      'Full engine · L3 reasoning · L6 weights · conviction formula',
      'Position data from D3 proprietary trading',
      'Any detail that would allow replication of the engine',
      '── PRICING MODEL (owner decides at trigger) ─────────────────────',
      'Subscription tier for signal access',
      'OR performance-based fee on returns generated',
      'Decision deferred to Phase D+ based on market conditions',
    ],
    prereqs: [
      'D3-INNER-CIRCLE running 12+ months ✅',
      'Both IBKR and ONDO adapters tested ✅',
      'Legal review complete ✅',
      'Owner explicit approval ✅',
    ],
    doD: [],
    smokeChecks: [],
    files: [],
    vendorDeps: [
      { name: 'IBKR', plan: 'Existing adapter', status: 'Extend to member accounts' },
      { name: 'ONDO', plan: 'New adapter', status: 'Build adapter at D3-COMMERCIAL start' },
    ],
    d3Signal: 'Commercial intelligence layer — D3 signals distributed via broker adapter to member accounts.',
  },

];  // end FUTURE_SESSIONS
